/**
 * AdminReferral.tsx — Complete admin referral management page
 *
 * Pool split logic:
 *   70%  → contributor pool, distributed to all downline by total_deposits
 *   30%  → achiever pool, split equally among achiever + 0/1/2 admin-selected minor leaders:
 *            0 leaders → achiever gets 100% of pool (e.g. 30 XRP)
 *            1 leader  → achiever + leader1 get 50% each (e.g. 15 + 15)
 *            2 leaders → achiever + leader1 + leader2 get 33.33% each (e.g. 10 + 10 + 10)
 *   Upline referrer cut — always separate & additional, unchanged
 */
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import Header from "../../components/Layout/Header";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import Modal from "../../components/UI/Modal";
import Alert from "../../components/UI/Alert";
import globalStyle from "../../styles/globalStyle";
import api from "../../services/api";
import { formatXRP } from "../../utils/formatters";
import {
  CurrencyDollarIcon,
  UsersIcon,
  CogIcon,
  ArrowTrendingUpIcon,
  PencilIcon,
  CheckCircleIcon,
  UserGroupIcon,
  XMarkIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  NoSymbolIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  FireIcon,
  ChartPieIcon,
  InformationCircleIcon,
  StarIcon,
} from "@heroicons/react/24/outline";
import BottomNav from "../../components/Layout/BottomNav";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReferralSettings {
  levels: number | string;
  commission_levels?: number | string;
  volume_depth_levels?: number | string;
  depth_rates?: Record<number, number>;
  rates: Record<number, number>;
  depth_weights?: Record<number, number>;
  level_bonuses: Record<number, number>;
  level_names: Record<number, string>;
  level_requirements: Record<number, { min_volume: number }>;
  signup_bonus: number;
  is_active: boolean;
  daily_commission_limit: number;
  monthly_commission_limit: number;
  first_place_bonus: number;
  second_place_bonus: number;
  third_place_bonus: number;
  payout_schedule: string;
  updated_at: string;
  level_bonus_referrer_pct: number;
  heat_top_threshold?: number;
  heat_mid_threshold?: number;
  level_bonus_pool_enabled: boolean;
  level_bonus_contributor_pct: number;
  level_bonus_achiever_pct: number;
}

interface ReferralStats {
  total_referrals: number;
  total_team_volume: number;
  total_referral_earnings: number;
  active_referrers: number;
}

interface LeaderboardEntry {
  rank: number;
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  total_referrals: number;
  team_volume: number;
  total_sales?: number;
  total_earnings: number;
}

interface ReferralTreeNode {
  id: number;
  username: string;
  name: string;
  depth: number;
  commission_pct?: number;
  volume_weight?: number;
  volume_weight_pct?: number;
  first_deposit: number;
  total_deposits: number;
  weighted_contribution?: number;
  team_volume: number;
  xrp_balance: number;
  joined_at: string;
  is_active: boolean;
  children: ReferralTreeNode[];
}

interface VolumeBreakdownRow {
  members: number;
  raw_deposits: number;
  weight: number;
  weighted_volume: number;
  depth_label?: string;
}

interface LevelProgressData {
  level: number;
  level_name: string;
  bonus: number;
  current_threshold: number;
  next_threshold: number | null;
  progress_volume: number;
  needed_volume: number;
  progress_pct: number;
  at_max: boolean;
}

interface PoolPreview {
  gross_bonus: number;
  contributor_pool: number;
  contributor_pct: number;
  achiever_pool: number;
  achiever_pool_pct: number;
  total_downline_deposits: number;
  contributors_count: number;
  split_preview: Record<0 | 1 | 2, number>;
}

interface DownlineMember {
  user_id: number;
  username: string;
  name: string;
  total_deposits: number;
  depth: number;
}

interface PendingLevelBonus {
  referral_id: number;
  user_id: number;
  username: string;
  current_level: number;
  pending_levels: number[];
  total_pending: number;
  team_volume: number;
  level_progress: Record<number, LevelProgressData>;
  pool_enabled: boolean;
  pool_previews: Record<number, PoolPreview>;
}

interface ApprovedLevelBonus {
  referral_id: number;
  user_id: number;
  username: string;
  current_level: number;
  awarded_levels: number[];
  total_awarded: number;
  team_volume: number;
  next_level_progress?: any;
  last_bonus_paid_at: string | null;
  pool_distributions?: Array<{
    level: number;
    gross_bonus: number;
    achiever_pool: number;
    achiever_pool_pct: number;
    per_person_achiever_share: number;
    split_count: number;
    minor_leaders: Array<{ user_id: number; username: string; amount: number }>;
    contributor_pool: number;
    contributor_pool_actual: number;
    total_downline_deposits: number;
    contributors_paid: number;
    paid_at: string;
  }>;
}

interface DepositItem {
  id: number;
  user_id: number;
  username: string;
  email: string;
  amount: number;
  created_at: string;
  excluded: boolean;
  excluded_at: string | null;
  tx_id: string | null;
}

// ─── Leader picker state ──────────────────────────────────────────────────────

interface LeaderSelection {
  leaders: DownlineMember[];
  search: string;
  results: DownlineMember[];
  loading: boolean;
}

type LeaderMap = Record<string, LeaderSelection>;
const leaderKey = (userId: number, level: number) => `${userId}-${level}`;
const emptySelection = (): LeaderSelection => ({
  leaders: [],
  search: "",
  results: [],
  loading: false,
});

// ─── Heat map helpers ─────────────────────────────────────────────────────────

export type HeatTier = "top" | "mid" | "dormant";
export const HEAT_PALETTE = {
  top: {
    border: "#f97316",
    glow: "rgba(249,115,22,0.4)",
    text: "#fed7aa",
    bg: "rgba(249,115,22,0.15)",
    emoji: "🔥",
  },
  mid: {
    border: "#38bdf8",
    glow: "rgba(56,189,248,0.3)",
    text: "#bae6fd",
    bg: "rgba(14,165,233,0.12)",
    emoji: "💧",
  },
  dormant: {
    border: "#475569",
    glow: "rgba(71,85,105,0.3)",
    text: "#94a3b8",
    bg: "rgba(71,85,105,0.12)",
    emoji: "💤",
  },
};

export function flattenTree(nodes: any[]): any[] {
  const out: any[] = [];
  const walk = (ns: any[]) =>
    ns.forEach((n) => {
      out.push(n);
      walk(n.children ?? []);
    });
  walk(nodes);
  return out;
}

export function buildHeatMap(
  allNodes: any[],
  top = 0.2,
  mid = 0.7,
): Map<number, HeatTier> {
  const sorted = [...allNodes]
    .filter((n) => n.id !== -1)
    .sort(
      (a, b) => (b.weighted_contribution ?? 0) - (a.weighted_contribution ?? 0),
    );
  const total = sorted.length;
  const map = new Map<number, HeatTier>();
  sorted.forEach((n, i) => {
    const pct = total > 0 ? i / total : 1;
    map.set(n.id, pct < top ? "top" : pct < mid ? "mid" : "dormant");
  });
  return map;
}

const DEPTH_PALETTE: Record<
  number,
  { border: string; glow: string; text: string; bg: string }
> = {
  0: {
    border: "#818cf8",
    glow: "rgba(129,140,248,0.30)",
    text: "#c7d2fe",
    bg: "rgba(99,102,241,0.12)",
  },
  1: {
    border: "#60a5fa",
    glow: "rgba(96,165,250,0.25)",
    text: "#93c5fd",
    bg: "rgba(59,130,246,0.10)",
  },
  2: {
    border: "#4ade80",
    glow: "rgba(74,222,128,0.25)",
    text: "#86efac",
    bg: "rgba(34,197,94,0.10)",
  },
  3: {
    border: "#fbbf24",
    glow: "rgba(251,191,36,0.25)",
    text: "#fde68a",
    bg: "rgba(245,158,11,0.10)",
  },
  4: {
    border: "#fb923c",
    glow: "rgba(251,146,60,0.25)",
    text: "#fed7aa",
    bg: "rgba(249,115,22,0.10)",
  },
  5: {
    border: "#c084fc",
    glow: "rgba(192,132,252,0.25)",
    text: "#e9d5ff",
    bg: "rgba(168,85,247,0.10)",
  },
  6: {
    border: "#f472b6",
    glow: "rgba(244,114,182,0.25)",
    text: "#fbcfe8",
    bg: "rgba(236,72,153,0.10)",
  },
  7: {
    border: "#34d399",
    glow: "rgba(52,211,153,0.25)",
    text: "#a7f3d0",
    bg: "rgba(16,185,129,0.10)",
  },
  8: {
    border: "#a78bfa",
    glow: "rgba(167,139,250,0.25)",
    text: "#ddd6fe",
    bg: "rgba(139,92,246,0.10)",
  },
  9: {
    border: "#f87171",
    glow: "rgba(248,113,113,0.25)",
    text: "#fecaca",
    bg: "rgba(239,68,68,0.10)",
  },
  10: {
    border: "#38bdf8",
    glow: "rgba(56,189,248,0.25)",
    text: "#bae6fd",
    bg: "rgba(14,165,233,0.10)",
  },
};
const dp = (d: number) => DEPTH_PALETTE[Math.min(d, 10)] ?? DEPTH_PALETTE[1];
const readDepthRates = (s: ReferralSettings) => s.depth_rates ?? s.rates ?? {};
const readDepthWeights = (s: ReferralSettings) => s.depth_weights ?? {};

function getFloorWeight(settings: ReferralSettings): number {
  const entries = Object.entries(readDepthWeights(settings)).sort(
    ([a], [b]) => Number(a) - Number(b),
  );
  if (!entries.length) return 0;
  return Number(entries[entries.length - 1][1]);
}

const safeParseInt = (val: unknown, def = 5): number => {
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const p = parseInt(val, 10);
    return isNaN(p) ? def : p;
  }
  return def;
};

const sw: React.CSSProperties = {
  overflowX: "auto",
  WebkitOverflowScrolling: "touch" as any,
};
const nw: React.CSSProperties = { whiteSpace: "nowrap" };

// ─── Loading spinner ──────────────────────────────────────────────────────────

const Spinner: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg
    className={globalStyle.loadingSpinner}
    viewBox="0 0 24 24"
    style={{ width: size, height: size }}
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
      fill="none"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const LoadingScreen: React.FC<{ message?: string }> = ({
  message = "Loading...",
}) => (
  <div className="w-full h-64 flex flex-col gap-2 items-center justify-center">
    <Spinner />
    <p className="mt-2 text-gray-400 text-sm">{message}</p>
  </div>
);

// ─── Pagination ───────────────────────────────────────────────────────────────

function usePagination<T>(items: T[], pageSize: number) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  useEffect(() => {
    setPage(1);
  }, [items.length]);
  return {
    page,
    setPage,
    totalPages,
    paged: items.slice((page - 1) * pageSize, page * pageSize),
    total: items.length,
  };
}

const Pagination: React.FC<{
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPage: (p: number) => void;
}> = ({ page, totalPages, total, pageSize, onPage }) => {
  if (totalPages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const base: React.CSSProperties = {
    width: 32,
    height: 32,
    borderRadius: 6,
    border: "1px solid rgba(255,255,255,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
  };
  const btn = (disabled: boolean): React.CSSProperties => ({
    ...base,
    background: disabled ? "transparent" : "rgba(255,255,255,0.05)",
    color: disabled ? "#334155" : "#94a3b8",
    cursor: disabled ? "not-allowed" : "pointer",
  });
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 16px",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        flexWrap: "wrap",
        gap: 8,
      }}
    >
      <span style={{ fontSize: 12, color: "#64748b" }}>
        {from}–{to} of {total}
      </span>
      <div style={{ display: "flex", gap: 4 }}>
        <button
          onClick={() => onPage(1)}
          disabled={page === 1}
          style={btn(page === 1)}
        >
          «
        </button>
        <button
          onClick={() => onPage(page - 1)}
          disabled={page === 1}
          style={btn(page === 1)}
        >
          ‹
        </button>
        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
          const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
          return p <= totalPages ? (
            <button
              key={p}
              onClick={() => onPage(p)}
              style={{
                ...base,
                border:
                  p === page
                    ? "1px solid #818cf8"
                    : "1px solid rgba(255,255,255,0.1)",
                background:
                  p === page
                    ? "rgba(99,102,241,0.2)"
                    : "rgba(255,255,255,0.04)",
                color: p === page ? "#c7d2fe" : "#94a3b8",
                cursor: "pointer",
                fontWeight: p === page ? 700 : 400,
              }}
            >
              {p}
            </button>
          ) : null;
        })}
        <button
          onClick={() => onPage(page + 1)}
          disabled={page === totalPages}
          style={btn(page === totalPages)}
        >
          ›
        </button>
        <button
          onClick={() => onPage(totalPages)}
          disabled={page === totalPages}
          style={btn(page === totalPages)}
        >
          »
        </button>
      </div>
    </div>
  );
};

// ─── Minor Leader Picker ──────────────────────────────────────────────────────

const MinorLeaderPicker: React.FC<{
  achieverPool: number;
  selection: LeaderSelection;
  onSearch: (q: string) => void;
  onSelect: (m: DownlineMember) => void;
  onRemove: (userId: number) => void;
}> = ({ achieverPool, selection, onSearch, onSelect, onRemove }) => {
  const leaderCount = selection.leaders.length;
  const splitCount = 1 + leaderCount;
  const perPersonShare = achieverPool / splitCount;

  const splitLabels: Record<number, string> = {
    0: `Achiever gets full ${formatXRP(achieverPool)} XRP (÷1)`,
    1: `Achiever + 1 leader → ${formatXRP(achieverPool / 2)} XRP each (÷2)`,
    2: `Achiever + 2 leaders → ${formatXRP(achieverPool / 3)} XRP each (÷3)`,
  };

  const barColors = ["#f97316", "#818cf8", "#c084fc"];

  return (
    <div
      style={{
        background: "rgba(99,102,241,0.06)",
        border: "1.5px solid rgba(99,102,241,0.22)",
        borderRadius: 10,
        padding: "12px 13px",
        marginTop: 8,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          marginBottom: 9,
        }}
      >
        <StarIcon
          style={{ width: 13, height: 13, color: "#818cf8", flexShrink: 0 }}
        />
        <span style={{ fontSize: 11, color: "#c7d2fe", fontWeight: 700 }}>
          Minor Leader Split
        </span>
        <span style={{ marginLeft: "auto", fontSize: 9, color: "#475569" }}>
          optional · max 2
        </span>
      </div>

      {/* Split bar */}
      <div
        style={{
          display: "flex",
          height: 7,
          borderRadius: 99,
          overflow: "hidden",
          gap: 1,
          marginBottom: 5,
        }}
      >
        {Array.from({ length: splitCount }, (_, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              background: barColors[i] ?? "#c084fc",
              borderRadius: 99,
            }}
          />
        ))}
      </div>
      <p style={{ fontSize: 10, color: "#94a3b8", marginBottom: 9 }}>
        {splitLabels[leaderCount]}
      </p>

      {/* Selected leaders */}
      {selection.leaders.length > 0 && (
        <div
          style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 9 }}
        >
          {selection.leaders.map((leader, idx) => (
            <div
              key={leader.user_id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background:
                  idx === 0
                    ? "rgba(129,140,248,0.14)"
                    : "rgba(192,132,252,0.14)",
                border: `1.5px solid ${idx === 0 ? "#818cf8" : "#c084fc"}`,
                borderRadius: 20,
                padding: "4px 10px",
              }}
            >
              <div
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  background:
                    idx === 0
                      ? "rgba(129,140,248,0.2)"
                      : "rgba(192,132,252,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 8,
                  fontWeight: 800,
                  color: idx === 0 ? "#c7d2fe" : "#e9d5ff",
                  flexShrink: 0,
                }}
              >
                {leader.username.charAt(0).toUpperCase()}
              </div>
              <span
                style={{
                  fontSize: 11,
                  color: idx === 0 ? "#c7d2fe" : "#e9d5ff",
                  fontWeight: 600,
                }}
              >
                @{leader.username}
              </span>
              <span style={{ fontSize: 9, color: "#64748b" }}>
                F{leader.depth}
              </span>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: idx === 0 ? "#818cf8" : "#c084fc",
                }}
              >
                {formatXRP(perPersonShare)} XRP
              </span>
              <button
                onClick={() => onRemove(leader.user_id)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#64748b",
                  cursor: "pointer",
                  padding: 0,
                  lineHeight: 1,
                  marginLeft: 1,
                }}
              >
                <XMarkIcon style={{ width: 11, height: 11 }} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search to add (only when < 2 selected) */}
      {leaderCount < 2 && (
        <div>
          <div style={{ position: "relative" }}>
            <MagnifyingGlassIcon
              style={{
                position: "absolute",
                left: 8,
                top: "50%",
                transform: "translateY(-50%)",
                width: 12,
                height: 12,
                color: "#64748b",
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              value={selection.search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={`Search downline — add leader ${leaderCount + 1}…`}
              style={{
                width: "100%",
                paddingLeft: 27,
                paddingRight: selection.search ? 26 : 10,
                paddingTop: 6,
                paddingBottom: 6,
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.09)",
                borderRadius: 7,
                color: "#f1f5f9",
                fontSize: 11,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            {selection.search && (
              <button
                onClick={() => onSearch("")}
                style={{
                  position: "absolute",
                  right: 7,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: "#64748b",
                  cursor: "pointer",
                  fontSize: 13,
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            )}
          </div>

          {/* Results dropdown */}
          {selection.search.trim() && (
            <div
              style={{
                marginTop: 3,
                background: "#0f172a",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 8,
                overflow: "hidden",
                maxHeight: 160,
                overflowY: "auto",
              }}
            >
              {selection.loading ? (
                <div
                  style={{
                    padding: 10,
                    textAlign: "center",
                    color: "#64748b",
                    fontSize: 11,
                  }}
                >
                  Searching…
                </div>
              ) : selection.results.filter(
                  (r) =>
                    !selection.leaders.some((l) => l.user_id === r.user_id),
                ).length === 0 ? (
                <div
                  style={{
                    padding: 10,
                    textAlign: "center",
                    color: "#64748b",
                    fontSize: 11,
                  }}
                >
                  No matching downline members
                </div>
              ) : (
                selection.results
                  .filter(
                    (r) =>
                      !selection.leaders.some((l) => l.user_id === r.user_id),
                  )
                  .slice(0, 8)
                  .map((member) => (
                    <button
                      key={member.user_id}
                      onClick={() => onSelect(member)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "7px 11px",
                        background: "transparent",
                        border: "none",
                        borderBottom: "1px solid rgba(255,255,255,0.04)",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          "rgba(99,102,241,0.1)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <div
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          background: "rgba(99,102,241,0.15)",
                          border: "1.5px solid #818cf8",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 10,
                          fontWeight: 800,
                          color: "#c7d2fe",
                          flexShrink: 0,
                        }}
                      >
                        {member.username.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#f1f5f9",
                            margin: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          @{member.username}
                        </p>
                        {member.name && (
                          <p
                            style={{ fontSize: 9, color: "#64748b", margin: 0 }}
                          >
                            {member.name}
                          </p>
                        )}
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <p
                          style={{
                            fontSize: 10,
                            color: "#fbbf24",
                            margin: 0,
                            fontWeight: 700,
                          }}
                        >
                          {formatXRP(member.total_deposits)} XRP
                        </p>
                        <p style={{ fontSize: 9, color: "#334155", margin: 0 }}>
                          F{member.depth}
                        </p>
                      </div>
                      <span
                        style={{
                          fontSize: 9,
                          background: "rgba(34,197,94,0.12)",
                          border: "1px solid rgba(74,222,128,0.3)",
                          borderRadius: 99,
                          padding: "2px 6px",
                          color: "#4ade80",
                          flexShrink: 0,
                        }}
                      >
                        +Add
                      </span>
                    </button>
                  ))
              )}
            </div>
          )}

          {leaderCount === 0 && !selection.search && (
            <p style={{ fontSize: 9, color: "#334155", marginTop: 5 }}>
              Leave empty → achiever keeps full {formatXRP(achieverPool)} XRP ·
              Add 1 leader → {formatXRP(achieverPool / 2)} each · Add 2 →{" "}
              {formatXRP(achieverPool / 3)} each
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Tree components ──────────────────────────────────────────────────────────

const TreeCard: React.FC<{
  node: ReferralTreeNode;
  isRoot?: boolean;
  highlighted?: boolean;
  heatMode?: boolean;
  heatMap?: Map<number, HeatTier>;
  onSelect: (n: ReferralTreeNode) => void;
  cardRef: (el: HTMLDivElement | null) => void;
}> = ({
  node,
  isRoot = false,
  highlighted = false,
  heatMode = false,
  heatMap,
  onSelect,
  cardRef,
}) => {
  const basePal = dp(isRoot ? 0 : node.depth);
  const heatTier = heatMode && heatMap ? heatMap.get(node.id) : null;
  const pal = heatTier ? HEAT_PALETTE[heatTier] : basePal;
  return (
    <div
      ref={cardRef}
      onClick={() => !isRoot && onSelect(node)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: isRoot ? "6px 12px" : "4px 10px",
        borderRadius: 99,
        border: `1.5px solid ${highlighted ? "#facc15" : pal.border}`,
        background: highlighted
          ? "linear-gradient(90deg,#1c1709,rgba(250,204,21,0.15))"
          : `linear-gradient(90deg,#0f172a,${pal.bg})`,
        boxShadow: highlighted
          ? "0 0 10px rgba(250,204,21,0.4)"
          : `0 0 6px ${pal.glow}`,
        cursor: isRoot ? "default" : "pointer",
        userSelect: "none",
        WebkitTapHighlightColor: "transparent",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {heatTier && !isRoot && (
        <span style={{ fontSize: 10, flexShrink: 0 }}>
          {HEAT_PALETTE[heatTier].emoji}
        </span>
      )}
      <div
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: node.is_active ? "#22c55e" : "#6b7280",
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontSize: isRoot ? 12 : 11,
          fontWeight: 700,
          color: "#f1f5f9",
          maxWidth: 100,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {node.username}
      </span>
      <span
        style={{
          fontSize: 8,
          fontWeight: 800,
          color: pal.text,
          background: "rgba(0,0,0,0.4)",
          border: `1px solid ${pal.border}`,
          borderRadius: 99,
          padding: "1px 5px",
          flexShrink: 0,
        }}
      >
        {isRoot ? "★" : `F${node.depth}`}
      </span>
    </div>
  );
};

const LRNode: React.FC<{
  node: ReferralTreeNode;
  isRoot?: boolean;
  expanded: Set<number>;
  onToggle: (id: number) => void;
  onSelect: (n: ReferralTreeNode) => void;
  matchIds: Set<number>;
  heatMode?: boolean;
  heatMap?: Map<number, HeatTier>;
  registerRef: (id: number, el: HTMLDivElement | null) => void;
}> = ({
  node,
  isRoot = false,
  expanded,
  onToggle,
  onSelect,
  matchIds,
  heatMode = false,
  heatMap,
  registerRef,
}) => {
  const hasChildren = (node.children?.length ?? 0) > 0;
  const isExpanded = expanded.has(node.id);
  const pal = dp(isRoot ? 0 : node.depth);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <TreeCard
          node={node}
          isRoot={isRoot}
          highlighted={matchIds.has(node.id)}
          heatMode={heatMode}
          heatMap={heatMap}
          onSelect={onSelect}
          cardRef={(el) => registerRef(node.id, el)}
        />
        {hasChildren && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggle(node.id);
              }}
              style={{
                marginTop: 6,
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: `1.5px solid ${pal.border}`,
                background: "#0f172a",
                color: pal.text,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {isExpanded ? "−" : "+"}
            </button>
            {!isExpanded && (
              <div style={{ fontSize: 9, color: "#475569", marginTop: 2 }}>
                {node.children.length}
              </div>
            )}
          </>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div
          style={{
            marginLeft: 56,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            paddingTop: 2,
          }}
        >
          {node.children.map((child) => (
            <LRNode
              key={child.id}
              node={child}
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
              matchIds={matchIds}
              heatMode={heatMode}
              heatMap={heatMap}
              registerRef={registerRef}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SVGLines: React.FC<{
  root: ReferralTreeNode | null;
  expanded: Set<number>;
  refs: Map<number, HTMLDivElement>;
  containerEl: HTMLDivElement | null;
  heatMode?: boolean;
  heatMap?: Map<number, HeatTier>;
}> = ({ root, expanded, refs, containerEl, heatMode = false, heatMap }) => {
  const [paths, setPaths] = useState<
    Array<{ d: string; color: string; key: string }>
  >([]);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    if (!root || !containerEl) return;
    const t = setTimeout(() => {
      const cr = containerEl.getBoundingClientRect();
      const result: typeof paths = [];
      let maxX = 0,
        maxY = 0;
      const visit = (node: ReferralTreeNode) => {
        if (!expanded.has(node.id)) return;
        const pEl = refs.get(node.id);
        if (!pEl) return;
        const pr = pEl.getBoundingClientRect();
        const px = pr.right - cr.left + containerEl.scrollLeft;
        const py = pr.top + pr.height / 2 - cr.top + containerEl.scrollTop;
        for (const child of node.children ?? []) {
          const cEl = refs.get(child.id);
          if (cEl) {
            const cr2 = cEl.getBoundingClientRect();
            const cx = cr2.left - cr.left + containerEl.scrollLeft;
            const cy =
              cr2.top + cr2.height / 2 - cr.top + containerEl.scrollTop;
            const mx = (cx - px) * 0.55;
            let lineColor = dp(child.depth ?? 1).border + "99";
            if (heatMode && heatMap) {
              const ht = heatMap.get(child.id);
              if (ht) lineColor = HEAT_PALETTE[ht].border + "99";
            }
            result.push({
              d: `M${px},${py} C${px + mx},${py} ${cx - mx},${cy} ${cx},${cy}`,
              color: lineColor,
              key: `${node.id}-${child.id}`,
            });
            maxX = Math.max(maxX, cx);
            maxY = Math.max(maxY, cy);
          }
          visit(child);
        }
      };
      visit(root);
      setPaths(result);
      setSize({ w: maxX + 40, h: maxY + 40 });
    }, 40);
    return () => clearTimeout(t);
  }, [root, expanded, refs, containerEl, heatMode, heatMap]);
  if (!paths.length) return null;
  return (
    <svg
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: size.w,
        height: size.h,
        pointerEvents: "none",
        overflow: "visible",
      }}
    >
      {paths.map((p) => (
        <path
          key={p.key}
          d={p.d}
          stroke={p.color}
          strokeWidth={1.5}
          fill="none"
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
};

const LRTree: React.FC<{
  rootNode: ReferralTreeNode;
  expanded: Set<number>;
  onToggle: (id: number) => void;
  onSelect: (n: ReferralTreeNode) => void;
  heatMode?: boolean;
  heatMap?: Map<number, HeatTier>;
}> = ({
  rootNode,
  expanded,
  onToggle,
  onSelect,
  heatMode = false,
  heatMap,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const refsMap = useRef<Map<number, HTMLDivElement>>(new Map());
  const [search, setSearch] = useState("");
  const [tick, setTick] = useState(0);
  const registerRef = useCallback((id: number, el: HTMLDivElement | null) => {
    if (el) refsMap.current.set(id, el);
    else refsMap.current.delete(id);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setTick((n) => n + 1), 60);
    return () => clearTimeout(t);
  }, [expanded]);
  const matchIds = useMemo(() => {
    if (!search.trim()) return new Set<number>();
    const q = search.toLowerCase();
    const ids = new Set<number>();
    const walk = (n: ReferralTreeNode) => {
      if (
        n.username?.toLowerCase().includes(q) ||
        n.name?.toLowerCase().includes(q)
      )
        ids.add(n.id);
      n.children?.forEach(walk);
    };
    walk(rootNode);
    return ids;
  }, [search, rootNode]);
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          flexShrink: 0,
          overflowX: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 14px",
            minWidth: "max-content",
          }}
        >
          <div style={{ position: "relative", width: 180, flexShrink: 0 }}>
            <MagnifyingGlassIcon
              style={{
                position: "absolute",
                left: 9,
                top: "50%",
                transform: "translateY(-50%)",
                width: 13,
                height: 13,
                color: "#64748b",
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search members..."
              style={{
                width: "100%",
                paddingLeft: 28,
                paddingRight: search ? 26 : 10,
                paddingTop: 5,
                paddingBottom: 5,
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.09)",
                borderRadius: 7,
                color: "#f1f5f9",
                fontSize: 12,
                outline: "none",
              }}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                style={{
                  position: "absolute",
                  right: 7,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: "#64748b",
                  cursor: "pointer",
                  fontSize: 14,
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                ×
              </button>
            )}
          </div>
          {search && (
            <span
              style={{
                fontSize: 11,
                color: matchIds.size > 0 ? "#818cf8" : "#ef4444",
                flexShrink: 0,
              }}
            >
              {matchIds.size > 0 ? `${matchIds.size} found` : "No match"}
            </span>
          )}
        </div>
      </div>
      <div
        ref={containerRef}
        style={{
          flex: 1,
          overflow: "auto",
          position: "relative",
          WebkitOverflowScrolling: "touch" as any,
        }}
      >
        <div
          style={{
            padding: "24px 32px 56px",
            minWidth: "max-content",
            position: "relative",
          }}
        >
          <SVGLines
            key={tick}
            root={rootNode}
            expanded={expanded}
            refs={refsMap.current}
            containerEl={containerRef.current}
            heatMode={heatMode}
            heatMap={heatMap}
          />
          <div style={{ position: "relative", zIndex: 1 }}>
            <LRNode
              node={rootNode}
              isRoot
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
              matchIds={matchIds}
              heatMode={heatMode}
              heatMap={heatMap}
              registerRef={registerRef}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const NodeDetailPanel: React.FC<{
  node: ReferralTreeNode;
  onClose: () => void;
}> = ({ node, onClose }) => {
  const pal = dp(node.depth);
  return (
    <div
      style={{
        position: "absolute",
        top: 16,
        right: 16,
        width: 280,
        background: "#0f172a",
        border: `2px solid ${pal.border}`,
        borderRadius: 16,
        boxShadow: `0 0 24px ${pal.glow},0 8px 32px rgba(0,0,0,0.8)`,
        zIndex: 20,
        padding: 16,
      }}
    >
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          background: "transparent",
          border: "none",
          color: "#64748b",
          cursor: "pointer",
          padding: 4,
        }}
      >
        <XMarkIcon style={{ width: 16, height: 16 }} />
      </button>
      <div className="flex items-center gap-3 mb-4">
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            border: `2.5px solid ${pal.border}`,
            background: pal.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            fontWeight: 800,
            color: pal.text,
          }}
        >
          {node.username?.charAt(0).toUpperCase()}
        </div>
        <div>
          <p className="text-sm font-bold text-white">{node.name}</p>
          <p className="text-xs text-gray-400">@{node.username}</p>
          <span
            style={{
              background: pal.bg,
              border: `1px solid ${pal.border}`,
              color: pal.text,
              fontSize: 10,
              fontWeight: 700,
              padding: "1px 7px",
              borderRadius: 99,
              display: "inline-block",
              marginTop: 4,
            }}
          >
            F{node.depth}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[
          {
            label: "Raw Deposits",
            val: `${formatXRP(node.total_deposits)} XRP`,
            color: "#60a5fa",
          },
          {
            label: "Their Team Vol",
            val: `${formatXRP(node.team_volume)} XRP`,
            color: "#c084fc",
          },
          {
            label: "1st Deposit",
            val: `${formatXRP(node.first_deposit)} XRP`,
            color: "#4ade80",
          },
          {
            label: "Direct Refs",
            val: String(node.children?.length || 0),
            color: "#fbbf24",
          },
        ].map(({ label, val, color }) => (
          <div
            key={label}
            style={{
              background: "rgba(255,255,255,0.04)",
              borderRadius: 8,
              padding: "8px 10px",
            }}
          >
            <p style={{ fontSize: 9, color: "#64748b", marginBottom: 2 }}>
              {label}
            </p>
            <p style={{ fontSize: 12, fontWeight: 700, color }}>{val}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const VolumeBreakdownTable: React.FC<{
  breakdown: Record<string | number, VolumeBreakdownRow>;
  totalVolume: number;
}> = ({ breakdown, totalVolume }) => {
  const rows = Object.entries(breakdown)
    .map(([key, row]) => ({
      key,
      depth: key === "∞" ? Infinity : Number(key),
      ...row,
    }))
    .filter((r) => r.members > 0)
    .sort((a, b) => a.depth - b.depth);
  if (!rows.length) return null;
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.02)",
        borderRadius: 10,
        border: "1px solid rgba(255,255,255,0.07)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "10px 14px",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <p style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8" }}>
          Volume Breakdown by Depth
        </p>
        <p style={{ fontSize: 11, color: "#fbbf24", fontWeight: 700 }}>
          Total: {formatXRP(totalVolume)} XRP
        </p>
      </div>
      <div style={sw}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 11,
            minWidth: 500,
          }}
        >
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
              {[
                "Depth",
                "Members",
                "Raw Deposits",
                "Weight",
                "Weighted Vol",
                "% of Total",
              ].map((h) => (
                <th
                  key={h}
                  style={{
                    padding: "7px 10px",
                    textAlign: "left",
                    color: "#64748b",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isFloor = row.key === "∞";
              const pal = isFloor ? dp(10) : dp(Number(row.key));
              const pct =
                totalVolume > 0 ? (row.weighted_volume / totalVolume) * 100 : 0;
              return (
                <tr
                  key={row.key}
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                >
                  <td style={{ padding: "7px 10px" }}>
                    <div
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: isFloor ? 6 : "50%",
                        border: `2px solid ${pal.border}`,
                        background: pal.bg,
                        color: pal.text,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 9,
                        fontWeight: 800,
                      }}
                    >
                      {isFloor ? (row.depth_label ?? "F∞") : `F${row.key}`}
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "7px 10px",
                      color: "#e2e8f0",
                      fontWeight: 600,
                    }}
                  >
                    {row.members}
                  </td>
                  <td
                    style={{
                      padding: "7px 10px",
                      color: "#93c5fd",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatXRP(row.raw_deposits)} XRP
                  </td>
                  <td style={{ padding: "7px 10px" }}>
                    <span
                      style={{
                        background: "rgba(245,158,11,0.12)",
                        border: "1px solid rgba(251,191,36,0.3)",
                        borderRadius: 6,
                        padding: "2px 7px",
                        color: "#fde68a",
                        fontWeight: 700,
                      }}
                    >
                      {(row.weight * 100).toFixed(0)}%{isFloor ? " ∞" : ""}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: "7px 10px",
                      color: "#fbbf24",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatXRP(row.weighted_volume)} XRP
                  </td>
                  <td style={{ padding: "7px 10px" }}>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <div
                        style={{
                          flex: 1,
                          height: 4,
                          background: "rgba(255,255,255,0.06)",
                          borderRadius: 99,
                          minWidth: 40,
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${Math.min(pct, 100)}%`,
                            background: pal.border,
                            borderRadius: 99,
                          }}
                        />
                      </div>
                      <span
                        style={{
                          color: "#94a3b8",
                          minWidth: 36,
                          textAlign: "right",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {pct.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const UserTreeModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  username: string;
  heatTopThreshold?: number;
  heatMidThreshold?: number;
}> = ({
  isOpen,
  onClose,
  userId,
  username,
  heatTopThreshold = 0.2,
  heatMidThreshold = 0.7,
}) => {
  const [tree, setTree] = useState<ReferralTreeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [selectedNode, setSelectedNode] = useState<ReferralTreeNode | null>(
    null,
  );
  const [userStats, setUserStats] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<"tree" | "breakdown">("tree");
  const [heatMode, setHeatMode] = useState(false);
  const [heatMap, setHeatMap] = useState<Map<number, HeatTier>>(new Map());

  const loadTree = useCallback(async () => {
    if (!isOpen || !userId) return;
    setLoading(true);
    setError(null);
    setSelectedNode(null);
    try {
      const resp = await api.get(`/admin/referral/user-tree/${userId}`);
      const addDepth = (ns: ReferralTreeNode[], d = 1): ReferralTreeNode[] =>
        ns.map((n) => ({
          ...n,
          depth: n.depth ?? d,
          children: addDepth(n.children || [], d + 1),
        }));
      const processed = addDepth(resp.data.tree || []);
      setTree(processed);
      const ids = new Set<number>([-1]);
      const collect = (ns: ReferralTreeNode[], depth: number) =>
        ns.forEach((n) => {
          if (depth <= 1) {
            ids.add(n.id);
            collect(n.children || [], depth + 1);
          }
        });
      collect(processed, 1);
      setExpanded(ids);
      if (resp.data.stats) setUserStats(resp.data.stats);
      setHeatMap(
        buildHeatMap(
          flattenTree(processed),
          heatTopThreshold,
          heatMidThreshold,
        ),
      );
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed to load tree");
    } finally {
      setLoading(false);
    }
  }, [isOpen, userId, heatTopThreshold, heatMidThreshold]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  const expandAll = () => {
    const ids = new Set<number>([-1]);
    const c = (ns: ReferralTreeNode[]) =>
      ns.forEach((n) => {
        ids.add(n.id);
        c(n.children || []);
      });
    c(tree);
    setExpanded(ids);
  };
  const collapseAll = () => setExpanded(new Set());

  const rootNode: ReferralTreeNode | null =
    tree.length > 0
      ? {
          id: -1,
          username,
          name: username,
          depth: 0,
          commission_pct: 0,
          volume_weight: 0,
          volume_weight_pct: 0,
          first_deposit: 0,
          total_deposits: 0,
          weighted_contribution: 0,
          team_volume: userStats?.team_volume ?? 0,
          xrp_balance: userStats?.xrp_balance ?? 0,
          joined_at: new Date().toISOString(),
          is_active: true,
          children: tree,
        }
      : null;

  if (!isOpen) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        background: "rgba(0,0,0,0.85)",
        backdropFilter: "blur(6px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          background: "#0f172a",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <div className="flex items-center gap-2">
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: "rgba(99,102,241,0.15)",
              border: "2px solid #818cf8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 800,
              color: "#c7d2fe",
              flexShrink: 0,
            }}
          >
            {username?.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-bold text-white">
              @{username}'s Network
            </p>
            {userStats && (
              <p className="text-xs text-gray-400">
                {userStats.total_referrals} direct ·{" "}
                {formatXRP(userStats.team_volume)} XRP weighted
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0 flex-wrap">
          {(["tree", "breakdown"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: "5px 10px",
                borderRadius: 7,
                background:
                  activeTab === tab
                    ? "rgba(99,102,241,0.2)"
                    : "rgba(255,255,255,0.06)",
                border:
                  activeTab === tab
                    ? "1px solid #818cf8"
                    : "1px solid rgba(255,255,255,0.09)",
                color: activeTab === tab ? "#c7d2fe" : "#9ca3af",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              {tab === "tree" ? "Tree" : "Breakdown"}
            </button>
          ))}
          {activeTab === "tree" && (
            <>
              <button
                onClick={() => setHeatMode(!heatMode)}
                style={{
                  padding: "5px 10px",
                  borderRadius: 7,
                  background: heatMode
                    ? "rgba(249,115,22,0.2)"
                    : "rgba(255,255,255,0.06)",
                  border: heatMode
                    ? "1px solid #f97316"
                    : "1px solid rgba(255,255,255,0.09)",
                  color: heatMode ? "#fed7aa" : "#9ca3af",
                  fontSize: 11,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <FireIcon style={{ width: 13, height: 13 }} />
                {heatMode ? "🔥 ON" : "Heat"}
              </button>
              {(["Expand", "Collapse"] as const).map((label) => (
                <button
                  key={label}
                  onClick={label === "Expand" ? expandAll : collapseAll}
                  style={{
                    padding: "5px 10px",
                    borderRadius: 7,
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.09)",
                    color: "#9ca3af",
                    fontSize: 11,
                    cursor: "pointer",
                  }}
                >
                  {label}
                </button>
              ))}
            </>
          )}
          <button
            onClick={onClose}
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.10)",
              color: "#9ca3af",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <XMarkIcon style={{ width: 17, height: 17 }} />
          </button>
        </div>
      </div>
      <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
        {loading ? (
          <LoadingScreen message="Loading network tree..." />
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-red-400 mb-3">{error}</p>
              <Button variant="secondary" onClick={loadTree}>
                Retry
              </Button>
            </div>
          </div>
        ) : activeTab === "breakdown" ? (
          <div style={{ height: "100%", overflow: "auto", padding: 16 }}>
            {userStats?.volume_breakdown ? (
              <VolumeBreakdownTable
                breakdown={userStats.volume_breakdown}
                totalVolume={userStats.team_volume}
              />
            ) : (
              <p className="text-gray-500 text-sm text-center py-10">
                No breakdown data.
              </p>
            )}
          </div>
        ) : !rootNode ? (
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <UserGroupIcon className="w-16 h-16 text-gray-600" />
            <p className="text-gray-400">@{username} has no referrals yet.</p>
          </div>
        ) : (
          <div style={{ position: "relative", height: "100%" }}>
            <LRTree
              rootNode={rootNode}
              expanded={expanded}
              onToggle={toggle}
              onSelect={(n) => {
                if (n.id !== -1) setSelectedNode(n);
              }}
              heatMode={heatMode}
              heatMap={heatMode ? heatMap : undefined}
            />
            {selectedNode && (
              <NodeDetailPanel
                node={selectedNode}
                onClose={() => setSelectedNode(null)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AdminReferral: React.FC = () => {
  const [settings, setSettings] = useState<ReferralSettings | null>(null);
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [pendingBonuses, setPendingBonuses] = useState<PendingLevelBonus[]>([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);
  const [approvingKey, setApprovingKey] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [approvedBonuses, setApprovedBonuses] = useState<ApprovedLevelBonus[]>(
    [],
  );
  const [loadingApproved, setLoadingApproved] = useState(false);
  const [treeOpen, setTreeOpen] = useState(false);
  const [treeUserId, setTreeUserId] = useState(0);
  const [treeUsername, setTreeUsername] = useState("");
  const [showExcludeModal, setShowExcludeModal] = useState(false);
  const [deposits, setDeposits] = useState<DepositItem[]>([]);
  const [depositSearch, setDepositSearch] = useState("");
  const [depositPage, setDepositPage] = useState(1);
  const [depositTotalPages, setDepositTotalPages] = useState(1);
  const [depositTotal, setDepositTotal] = useState(0);
  const [loadingDeposits, setLoadingDeposits] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [excludeSuccess, setExcludeSuccess] = useState<string | null>(null);
  const [lbSearch, setLbSearch] = useState("");

  // ── Leader picker state ──────────────────────────────────────────────────
  const [leaderMap, setLeaderMap] = useState<LeaderMap>({});
  const downlineCacheRef = useRef<Record<number, DownlineMember[]>>({});

  const [formData, setFormData] = useState({
    levels: 5,
    commission_levels: 5,
    volume_depth_levels: 10,
    depth_rates: {} as Record<number, string>,
    depth_weights: {} as Record<number, string>,
    level_bonuses: {} as Record<number, number>,
    level_names: {} as Record<number, string>,
    level_requirements: {} as Record<number, { min_volume: number }>,
    signup_bonus: "5",
    is_active: true,
    daily_commission_limit: "1000",
    monthly_commission_limit: "10000",
    first_place_bonus: "100",
    second_place_bonus: "50",
    third_place_bonus: "25",
    payout_schedule: "daily",
    level_bonus_referrer_pct: "0",
    recalculate_volumes: true,
    heat_top_threshold: "20",
    heat_mid_threshold: "70",
    level_bonus_pool_enabled: false,
    level_bonus_contributor_pct: "70",
    level_bonus_achiever_pct: "30",
  });

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const sr = await api.get("/admin/referral/settings");
      const s: ReferralSettings = sr.data.settings;
      setSettings(s);
      const lr = await api.get("/admin/referral/leaderboard");
      setLeaderboard(lr.data.leaderboard || []);
      setStats(lr.data.statistics || {});
      if (s) {
        const dR = readDepthRates(s);
        const dW = readDepthWeights(s);
        const cl =
          s.commission_levels !== undefined
            ? safeParseInt(s.commission_levels)
            : safeParseInt(s.levels);
        const vdl =
          s.volume_depth_levels !== undefined
            ? safeParseInt(s.volume_depth_levels, 10)
            : 10;
        const dr: Record<number, string> = {};
        for (let d = 1; d <= 15; d++)
          dr[d] = String(((dR[d] ?? 0) * 100).toFixed(2));
        const dw: Record<number, string> = {};
        for (let d = 1; d <= 15; d++)
          dw[d] = String(((dW[d] ?? 0) * 100).toFixed(2));
        setFormData({
          levels: Number(s.levels) || 5,
          commission_levels: cl,
          volume_depth_levels: vdl,
          depth_rates: dr,
          depth_weights: dw,
          level_bonuses: s.level_bonuses || {},
          level_names: s.level_names || {},
          level_requirements: s.level_requirements || {},
          signup_bonus: String(s.signup_bonus ?? 5),
          is_active: s.is_active,
          daily_commission_limit: String(s.daily_commission_limit ?? 1000),
          monthly_commission_limit: String(s.monthly_commission_limit ?? 10000),
          first_place_bonus: String(s.first_place_bonus ?? 100),
          second_place_bonus: String(s.second_place_bonus ?? 50),
          third_place_bonus: String(s.third_place_bonus ?? 25),
          payout_schedule: s.payout_schedule || "daily",
          level_bonus_referrer_pct: String(
            ((s.level_bonus_referrer_pct ?? 0) * 100).toFixed(2),
          ),
          recalculate_volumes: true,
          heat_top_threshold: String(
            Math.round((s.heat_top_threshold ?? 0.2) * 100),
          ),
          heat_mid_threshold: String(
            Math.round((s.heat_mid_threshold ?? 0.7) * 100),
          ),
          level_bonus_pool_enabled: s.level_bonus_pool_enabled ?? false,
          level_bonus_contributor_pct: String(
            Math.round((s.level_bonus_contributor_pct ?? 0.7) * 100),
          ),
          level_bonus_achiever_pct: String(
            Math.round((s.level_bonus_achiever_pct ?? 0.3) * 100),
          ),
        });
      }
      await loadPendingBonuses();
      await loadApprovedBonuses();
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed to load referral data");
    } finally {
      setLoading(false);
    }
  };

  const loadPendingBonuses = async () => {
    try {
      setLoadingPending(true);
      const r = await api.get("/admin/referral/pending-level-bonuses");
      setPendingBonuses(r.data.pending || []);
    } catch {
      setPendingBonuses([]);
    } finally {
      setLoadingPending(false);
    }
  };
  const loadApprovedBonuses = async () => {
    try {
      setLoadingApproved(true);
      const r = await api.get("/admin/referral/approved-level-bonuses");
      setApprovedBonuses(r.data.approved || []);
    } catch {
      setApprovedBonuses([]);
    } finally {
      setLoadingApproved(false);
    }
  };

  const loadDeposits = async (page = 1, search = depositSearch) => {
    try {
      setLoadingDeposits(true);
      const params = new URLSearchParams({ page: String(page), limit: "20" });
      if (search.trim()) params.set("search", search.trim());
      const r = await api.get(`/admin/referral/deposits?${params}`);
      setDeposits(r.data.deposits || []);
      setDepositPage(r.data.current_page || 1);
      setDepositTotalPages(r.data.last_page || 1);
      setDepositTotal(r.data.total || 0);
    } catch {
      setDeposits([]);
    } finally {
      setLoadingDeposits(false);
    }
  };

  // ── Leader picker helpers ────────────────────────────────────────────────

  const loadDownlineMembers = useCallback(
    async (userId: number): Promise<DownlineMember[]> => {
      if (downlineCacheRef.current[userId])
        return downlineCacheRef.current[userId];
      try {
        const r = await api.get(`/admin/referral/downline-members/${userId}`);
        const members: DownlineMember[] = r.data.members || [];
        downlineCacheRef.current[userId] = members;
        return members;
      } catch {
        return [];
      }
    },
    [],
  );

  const getOrInit = useCallback(
    (userId: number, level: number): LeaderSelection =>
      leaderMap[leaderKey(userId, level)] ?? emptySelection(),
    [leaderMap],
  );

  const patchLeader = useCallback(
    (userId: number, level: number, patch: Partial<LeaderSelection>) => {
      const k = leaderKey(userId, level);
      setLeaderMap((prev) => ({
        ...prev,
        [k]: { ...(prev[k] ?? emptySelection()), ...patch },
      }));
    },
    [],
  );

  const handleLeaderSearch = useCallback(
    async (userId: number, level: number, query: string) => {
      patchLeader(userId, level, {
        search: query,
        loading: query.trim().length > 0,
      });
      if (!query.trim()) {
        patchLeader(userId, level, { results: [], loading: false });
        return;
      }
      const all = await loadDownlineMembers(userId);
      const q = query.toLowerCase();
      patchLeader(userId, level, {
        results: all.filter(
          (m) =>
            m.username.toLowerCase().includes(q) ||
            (m.name && m.name.toLowerCase().includes(q)),
        ),
        loading: false,
      });
    },
    [loadDownlineMembers, patchLeader],
  );

  const handleLeaderSelect = useCallback(
    (userId: number, level: number, member: DownlineMember) => {
      const cur = leaderMap[leaderKey(userId, level)] ?? emptySelection();
      if (
        cur.leaders.length >= 2 ||
        cur.leaders.some((l) => l.user_id === member.user_id)
      )
        return;
      patchLeader(userId, level, {
        leaders: [...cur.leaders, member],
        search: "",
        results: [],
      });
    },
    [leaderMap, patchLeader],
  );

  const handleLeaderRemove = useCallback(
    (userId: number, level: number, leaderId: number) => {
      const cur = leaderMap[leaderKey(userId, level)] ?? emptySelection();
      patchLeader(userId, level, {
        leaders: cur.leaders.filter((l) => l.user_id !== leaderId),
      });
    },
    [leaderMap, patchLeader],
  );

  // ── Approve level bonus ──────────────────────────────────────────────────

  const approveLevelBonus = async (userId: number, level: number) => {
    const k = leaderKey(userId, level);
    const selection = leaderMap[k];
    const leaderIds = selection?.leaders.map((l) => l.user_id) ?? [];
    const aKey = `${userId}-${level}`;
    try {
      setApprovingKey(aKey);
      const r = await api.post(
        `/admin/referral/approve-level-bonus/${userId}/${level}`,
        { minor_leader_ids: leaderIds },
      );
      setSuccess(r.data.message ?? `Level ${level} bonus approved`);
      setLeaderMap((prev) => {
        const n = { ...prev };
        delete n[k];
        return n;
      });
      await loadPendingBonuses();
      await loadApprovedBonuses();
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed to approve bonus");
    } finally {
      setApprovingKey(null);
    }
  };

  const approveAll = async () => {
    try {
      setApprovingAll(true);
      const r = await api.post("/admin/referral/approve-all-level-bonuses");
      setSuccess(
        `Approved ${r.data.paid} bonus(es). Note: bulk approve has no minor leaders — use individual approve to assign leaders.`,
      );
      await loadPendingBonuses();
      await loadApprovedBonuses();
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed");
    } finally {
      setApprovingAll(false);
    }
  };

  const scanAll = async () => {
    try {
      setScanning(true);
      const r = await api.post("/admin/referral/scan-level-bonuses");
      const q = r.data.queued ?? 0;
      setSuccess(
        q > 0
          ? `Scan complete — ${q} new bonus${q !== 1 ? "es" : ""} queued.`
          : "Scan complete — no new bonuses.",
      );
      await loadPendingBonuses();
      await loadApprovedBonuses();
    } catch (e: any) {
      setError(e.response?.data?.message || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const handleRecalculateAll = async () => {
    try {
      setRecalculating(true);
      const r = await api.post("/admin/referral/recalculate-volumes");
      setSuccess(r.data.message);
      await loadData();
    } catch (e: any) {
      setError(e.response?.data?.message || "Recalculation failed");
    } finally {
      setRecalculating(false);
    }
  };

  const toggleExclude = async (id: number) => {
    try {
      setTogglingId(id);
      const r = await api.post(`/admin/referral/toggle-exclude-deposit/${id}`);
      setExcludeSuccess(r.data.message);
      setDeposits((prev) =>
        prev.map((d) =>
          d.id === id
            ? {
                ...d,
                excluded: r.data.excluded,
                excluded_at: r.data.excluded ? new Date().toISOString() : null,
              }
            : d,
        ),
      );
      loadData();
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed");
    } finally {
      setTogglingId(null);
    }
  };

  const handleSaveSettings = async () => {
    const topPct = parseFloat(formData.heat_top_threshold) || 20;
    const midPct = parseFloat(formData.heat_mid_threshold) || 70;
    if (topPct >= midPct) {
      setError("Heat top threshold must be less than mid threshold");
      return;
    }
    const cPct = parseFloat(formData.level_bonus_contributor_pct) || 70;
    const aPct = parseFloat(formData.level_bonus_achiever_pct) || 30;
    if (
      formData.level_bonus_pool_enabled &&
      Math.abs(cPct + aPct - 100) > 0.01
    ) {
      setError("Contributor % + Achiever % must equal 100%");
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const dr: Record<number, number> = {};
      for (let d = 1; d <= 15; d++)
        dr[d] = (parseFloat(formData.depth_rates[d] || "0") || 0) / 100;
      const dw: Record<number, number> = {};
      for (let d = 1; d <= 15; d++)
        dw[d] = (parseFloat(formData.depth_weights[d] || "0") || 0) / 100;
      await api.put("/admin/referral/settings", {
        levels: formData.levels,
        commission_levels: formData.commission_levels,
        volume_depth_levels: formData.volume_depth_levels,
        depth_rates: dr,
        depth_weights: dw,
        level_bonuses: formData.level_bonuses,
        level_names: formData.level_names,
        level_requirements: formData.level_requirements,
        signup_bonus: parseFloat(formData.signup_bonus) || 0,
        is_active: formData.is_active,
        daily_commission_limit:
          parseFloat(formData.daily_commission_limit) || 0,
        monthly_commission_limit:
          parseFloat(formData.monthly_commission_limit) || 0,
        first_place_bonus: parseFloat(formData.first_place_bonus) || 0,
        second_place_bonus: parseFloat(formData.second_place_bonus) || 0,
        third_place_bonus: parseFloat(formData.third_place_bonus) || 0,
        payout_schedule: formData.payout_schedule,
        level_bonus_referrer_pct:
          (parseFloat(formData.level_bonus_referrer_pct) || 0) / 100,
        recalculate_volumes: formData.recalculate_volumes,
        heat_top_threshold: topPct / 100,
        heat_mid_threshold: midPct / 100,
        level_bonus_pool_enabled: formData.level_bonus_pool_enabled,
        level_bonus_contributor_pct: cPct / 100,
        level_bonus_achiever_pct: aPct / 100,
      });
      setSuccess(
        formData.recalculate_volumes
          ? "Settings saved & volumes recalculated"
          : "Settings saved",
      );
      setShowSettingsModal(false);
      loadData();
    } catch (e: any) {
      setError(e.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────

  const dRates = settings ? readDepthRates(settings) : {};
  const dWeights = settings ? readDepthWeights(settings) : {};
  const commLevels = settings
    ? settings.commission_levels !== undefined
      ? safeParseInt(settings.commission_levels)
      : safeParseInt(settings.levels)
    : 5;
  const volDepths = settings
    ? safeParseInt(settings.volume_depth_levels, 10)
    : 10;
  const refCutPct = settings
    ? (settings.level_bonus_referrer_pct ?? 0) * 100
    : 0;
  const poolOn = settings?.level_bonus_pool_enabled ?? false;
  const contribPct = settings
    ? (settings.level_bonus_contributor_pct ?? 0.7) * 100
    : 70;
  const achevrPct = settings
    ? (settings.level_bonus_achiever_pct ?? 0.3) * 100
    : 30;
  const floorW = settings ? getFloorWeight(settings) : 0;
  const heatTop = settings?.heat_top_threshold ?? 0.2;
  const heatMid = settings?.heat_mid_threshold ?? 0.7;
  const totalPendingCount = pendingBonuses.reduce(
    (s, p) => s + p.pending_levels.length,
    0,
  );

  const filteredLb = useMemo(() => {
    if (!lbSearch.trim()) return leaderboard;
    const q = lbSearch.toLowerCase();
    return leaderboard.filter(
      (e) =>
        e.username?.toLowerCase().includes(q) ||
        e.email?.toLowerCase().includes(q) ||
        e.first_name?.toLowerCase().includes(q) ||
        e.last_name?.toLowerCase().includes(q),
    );
  }, [leaderboard, lbSearch]);

  const pendingPag = usePagination(pendingBonuses, 10);
  const approvedPag = usePagination(approvedBonuses, 10);
  const lbPag = usePagination(filteredLb, 15);

  const liveC = parseFloat(formData.level_bonus_contributor_pct) || 0;
  const liveA = parseFloat(formData.level_bonus_achiever_pct) || 0;
  const splitOk = Math.abs(liveC + liveA - 100) <= 0.01;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={globalStyle.container}>
      <Header />
      <main className="page-content">
        {/* ── Page header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
          <div>
            <h1 className={globalStyle.h3}>Referral Management</h1>
            <p className={`${globalStyle.body2} text-gray-400`}>
              Manage referral program settings and analytics
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={handleRecalculateAll}
              loading={recalculating}
              leftIcon={<ArrowPathIcon className="w-4 h-4" />}
              variant="secondary"
              size="sm"
            >
              Recalculate Volumes
            </Button>
            <Button
              onClick={() => {
                setShowExcludeModal(true);
                loadDeposits(1, "");
              }}
              leftIcon={<NoSymbolIcon className="w-4 h-4" />}
              variant="secondary"
              size="sm"
            >
              Fix Test Deposits
            </Button>
            <Button
              onClick={() => setShowSettingsModal(true)}
              leftIcon={<CogIcon className="w-4 h-4" />}
              size="sm"
            >
              Settings
            </Button>
          </div>
        </div>

        {error && (
          <Alert
            type="error"
            message={error}
            onClose={() => setError(null)}
            className="mb-6"
          />
        )}
        {success && (
          <Alert
            type="success"
            message={success}
            onClose={() => setSuccess(null)}
            className="mb-6"
          />
        )}

        {/* ── Stats grid ── */}
        {loading ? (
          <div className="mb-6">
            <LoadingScreen message="Loading statistics..." />
          </div>
        ) : (
          stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[
                {
                  icon: UsersIcon,
                  bg: "blue",
                  label: "Total Referrals",
                  val: stats.total_referrals,
                },
                {
                  icon: CheckCircleIcon,
                  bg: "green",
                  label: "Active Referrers",
                  val: stats.active_referrers,
                },
                {
                  icon: CurrencyDollarIcon,
                  bg: "purple",
                  label: "Weighted Volume",
                  val: `${formatXRP(stats.total_team_volume)} XRP`,
                },
                {
                  icon: ArrowTrendingUpIcon,
                  bg: "yellow",
                  label: "Total Earnings",
                  val: `${formatXRP(stats.total_referral_earnings)} XRP`,
                },
              ].map(({ icon: Icon, bg, label, val }) => (
                <Card key={label} className="text-center">
                  <div
                    className={`w-10 h-10 rounded-full bg-${bg}-900 flex items-center justify-center mx-auto mb-2`}
                  >
                    <Icon className={`w-5 h-5 text-${bg}-300`} />
                  </div>
                  <p className="text-xs text-gray-400 mb-1">{label}</p>
                  <p className="text-xl font-bold text-white">{val}</p>
                </Card>
              ))}
            </div>
          )
        )}

        {/* ── Pool status banner ── */}
        {!loading && settings && (
          <div
            style={{
              background: poolOn
                ? "rgba(99,102,241,0.08)"
                : "rgba(255,255,255,0.03)",
              border: `1.5px solid ${poolOn ? "rgba(99,102,241,0.35)" : "rgba(255,255,255,0.07)"}`,
              borderRadius: 12,
              padding: "12px 16px",
              marginBottom: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <ChartPieIcon
                style={{
                  width: 20,
                  height: 20,
                  color: poolOn ? "#818cf8" : "#475569",
                  flexShrink: 0,
                }}
              />
              <div>
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: poolOn ? "#c7d2fe" : "#64748b",
                  }}
                >
                  Level Bonus Pool Split — {poolOn ? "ENABLED" : "DISABLED"}
                </p>
                {poolOn && (
                  <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                    <strong style={{ color: "#818cf8" }}>
                      {contribPct.toFixed(0)}%
                    </strong>{" "}
                    → all downline contributors ·
                    <strong style={{ color: "#f97316" }}>
                      {" "}
                      {achevrPct.toFixed(0)}%
                    </strong>{" "}
                    achiever pool ÷ (1 + minor leaders) equally
                    {refCutPct > 0 && (
                      <span style={{ color: "#c084fc" }}>
                        {" "}
                        · +<strong>{refCutPct.toFixed(1)}%</strong> upline
                        referrer (separate)
                      </span>
                    )}
                  </p>
                )}
                {!poolOn && (
                  <p style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>
                    Full bonus goes to achiever. Enable in Settings.
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => setShowSettingsModal(true)}
              leftIcon={<PencilIcon className="w-3 h-3" />}
            >
              Configure
            </Button>
          </div>
        )}

        {/* ── Pending level bonuses ── */}
        <Card className="mb-6" padding="lg">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2">
                <h3 className={globalStyle.h5}>Pending Level-Up Bonuses</h3>
                {pendingBonuses.length > 0 && (
                  <span
                    style={{
                      background: "rgba(251,191,36,0.15)",
                      border: "1.5px solid rgba(251,191,36,0.4)",
                      borderRadius: 99,
                      padding: "1px 10px",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#fde68a",
                    }}
                  >
                    {pendingBonuses.length}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Admin approval required.
                {poolOn && (
                  <span className="text-indigo-400 ml-1">
                    Optionally pick up to 2 minor leaders before approving —
                    they split the {achevrPct.toFixed(0)}% achiever pool
                    equally.
                  </span>
                )}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button
                variant="secondary"
                size="sm"
                onClick={scanAll}
                loading={scanning}
                leftIcon={<ArrowTrendingUpIcon className="w-4 h-4" />}
              >
                Scan All
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={async () => {
                  await loadPendingBonuses();
                  await loadApprovedBonuses();
                }}
                loading={loadingPending && !scanning}
              >
                Refresh
              </Button>
              {pendingBonuses.length > 0 && (
                <Button
                  size="sm"
                  onClick={approveAll}
                  loading={approvingAll}
                  leftIcon={<CheckCircleIcon className="w-4 h-4" />}
                >
                  Approve All ({totalPendingCount})
                </Button>
              )}
            </div>
          </div>

          {loadingPending && !scanning ? (
            <LoadingScreen message="Loading pending bonuses..." />
          ) : pendingBonuses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <CheckCircleIcon className="w-12 h-12 text-green-600" />
              <p className="text-gray-400 text-sm">
                No pending bonuses — all caught up!
              </p>
            </div>
          ) : (
            <div style={sw}>
              <table className="w-full" style={{ minWidth: 800 }}>
                <thead>
                  <tr className="border-b border-gray-800">
                    {[
                      "User",
                      "Live Tier",
                      "Level",
                      "Progress",
                      "Bonus & Split",
                      "Actions",
                    ].map((h) => (
                      <th
                        key={h}
                        className="py-3 px-4 text-left text-sm font-medium text-gray-400"
                        style={nw}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pendingPag.paged.map((pb) =>
                    pb.pending_levels.map((lvl, lvlIdx) => {
                      const aKey = `${pb.user_id}-${lvl}`;
                      const approving = approvingKey === aKey;
                      const prog = pb.level_progress?.[lvl];
                      const preview = pb.pool_previews?.[lvl];
                      const sel = getOrInit(pb.user_id, lvl);
                      const lc = sel.leaders.length; // 0, 1, 2
                      const sc = 1 + lc; // split count
                      const pps = preview
                        ? preview.split_preview[lc as 0 | 1 | 2]
                        : (settings?.level_bonuses[lvl] ?? 0);

                      return (
                        <tr
                          key={aKey}
                          className="border-b border-gray-800"
                          style={{ verticalAlign: "top" }}
                        >
                          {/* User — rowSpan across all levels of this achiever */}
                          {lvlIdx === 0 && (
                            <td
                              className="py-3 px-4"
                              rowSpan={pb.pending_levels.length}
                              style={{ verticalAlign: "middle" }}
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  style={{
                                    width: 30,
                                    height: 30,
                                    borderRadius: "50%",
                                    background: "rgba(251,191,36,0.15)",
                                    border: "2px solid #fbbf24",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 11,
                                    fontWeight: 800,
                                    color: "#fde68a",
                                    flexShrink: 0,
                                  }}
                                >
                                  {pb.username?.charAt(0).toUpperCase()}
                                </div>
                                <span
                                  className="font-medium text-white"
                                  style={nw}
                                >
                                  {pb.username}
                                </span>
                              </div>
                            </td>
                          )}

                          {/* Live tier — rowSpan */}
                          {lvlIdx === 0 && (
                            <td
                              className="py-3 px-4"
                              rowSpan={pb.pending_levels.length}
                              style={{ verticalAlign: "middle" }}
                            >
                              <span
                                style={{
                                  background: "rgba(99,102,241,0.15)",
                                  border: "1.5px solid #818cf8",
                                  borderRadius: 6,
                                  padding: "2px 10px",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: "#c7d2fe",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                L{pb.current_level}
                              </span>
                              <p
                                style={{
                                  fontSize: 9,
                                  color: "#334155",
                                  marginTop: 3,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {formatXRP(pb.team_volume)} XRP
                              </p>
                            </td>
                          )}

                          {/* Level badge */}
                          <td className="py-3 px-4" style={nw}>
                            <span
                              style={{
                                background: "rgba(251,191,36,0.12)",
                                border: "1.5px solid rgba(251,191,36,0.4)",
                                borderRadius: 6,
                                padding: "2px 8px",
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#fde68a",
                              }}
                            >
                              L{lvl}
                            </span>
                          </td>

                          {/* Progress */}
                          <td className="py-3 px-4" style={{ minWidth: 160 }}>
                            {prog && (
                              <div>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    marginBottom: 3,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 10,
                                      color: "#fde68a",
                                      fontWeight: 700,
                                    }}
                                  >
                                    → {prog.at_max ? "Max" : `L${lvl + 1}`}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      color:
                                        prog.progress_pct >= 100
                                          ? "#4ade80"
                                          : "#94a3b8",
                                    }}
                                  >
                                    {prog.progress_pct.toFixed(1)}%
                                  </span>
                                </div>
                                <div
                                  style={{
                                    height: 5,
                                    background: "rgba(255,255,255,0.07)",
                                    borderRadius: 99,
                                    overflow: "hidden",
                                    marginBottom: 3,
                                  }}
                                >
                                  <div
                                    style={{
                                      height: "100%",
                                      width: `${Math.min(prog.progress_pct, 100)}%`,
                                      background:
                                        prog.progress_pct >= 100
                                          ? "#4ade80"
                                          : "#818cf8",
                                      borderRadius: 99,
                                    }}
                                  />
                                </div>
                                <span style={{ fontSize: 9, color: "#64748b" }}>
                                  {formatXRP(prog.progress_volume)} /{" "}
                                  {formatXRP(prog.needed_volume)} XRP
                                </span>
                              </div>
                            )}
                          </td>

                          {/* Bonus & Split */}
                          <td
                            className="py-3 px-4"
                            style={{ minWidth: poolOn ? 260 : 140 }}
                          >
                            {poolOn && preview ? (
                              <div>
                                <p
                                  style={{
                                    fontSize: 10,
                                    color: "#64748b",
                                    marginBottom: 5,
                                  }}
                                >
                                  Gross:{" "}
                                  <strong style={{ color: "#f1f5f9" }}>
                                    {formatXRP(preview.gross_bonus)} XRP
                                  </strong>
                                  {refCutPct > 0 && (
                                    <span
                                      style={{
                                        color: "#c084fc",
                                        marginLeft: 4,
                                      }}
                                    >
                                      +
                                      {formatXRP(
                                        (preview.gross_bonus * refCutPct) / 100,
                                      )}{" "}
                                      XRP referrer
                                    </span>
                                  )}
                                </p>
                                {/* Contributor pool */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    marginBottom: 6,
                                    background: "rgba(99,102,241,0.06)",
                                    borderRadius: 6,
                                    padding: "5px 8px",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      background: "#818cf8",
                                      flexShrink: 0,
                                    }}
                                  />
                                  <span
                                    style={{ fontSize: 10, color: "#c7d2fe" }}
                                  >
                                    👥 {formatXRP(preview.contributor_pool)} XRP
                                    → {preview.contributors_count} contributors
                                    ({preview.contributor_pct.toFixed(0)}%)
                                  </span>
                                </div>
                                {/* Achiever pool + leader picker */}
                                <div
                                  style={{
                                    background: "rgba(249,115,22,0.06)",
                                    border: "1px solid rgba(249,115,22,0.2)",
                                    borderRadius: 8,
                                    padding: "8px 10px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      marginBottom: 4,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 10,
                                        color: "#fed7aa",
                                        fontWeight: 700,
                                      }}
                                    >
                                      🏆 Achiever pool:{" "}
                                      {formatXRP(preview.achiever_pool)} XRP (
                                      {preview.achiever_pool_pct.toFixed(0)}%)
                                    </span>
                                    <span
                                      style={{ fontSize: 9, color: "#64748b" }}
                                    >
                                      ÷{sc} = {formatXRP(pps)} each
                                    </span>
                                  </div>
                                  <MinorLeaderPicker
                                    achieverPool={preview.achiever_pool}
                                    selection={sel}
                                    onSearch={(q) =>
                                      handleLeaderSearch(pb.user_id, lvl, q)
                                    }
                                    onSelect={(m) =>
                                      handleLeaderSelect(pb.user_id, lvl, m)
                                    }
                                    onRemove={(id) =>
                                      handleLeaderRemove(pb.user_id, lvl, id)
                                    }
                                  />
                                </div>
                              </div>
                            ) : (
                              <div>
                                <p
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: "#4ade80",
                                  }}
                                >
                                  {formatXRP(settings?.level_bonuses[lvl] ?? 0)}{" "}
                                  XRP
                                </p>
                                {refCutPct > 0 && (
                                  <p
                                    style={{
                                      fontSize: 10,
                                      color: "#c084fc",
                                      marginTop: 1,
                                    }}
                                  >
                                    +
                                    {formatXRP(
                                      ((settings?.level_bonuses[lvl] ?? 0) *
                                        refCutPct) /
                                        100,
                                    )}{" "}
                                    XRP → referrer
                                  </p>
                                )}
                              </div>
                            )}
                          </td>

                          {/* Approve button */}
                          <td
                            className="py-3 px-4"
                            style={{ verticalAlign: "top", paddingTop: 14 }}
                          >
                            <button
                              onClick={() => approveLevelBonus(pb.user_id, lvl)}
                              disabled={approving || approvingAll}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                                background: "rgba(34,197,94,0.12)",
                                border: "1.5px solid rgba(74,222,128,0.4)",
                                borderRadius: 8,
                                padding: "6px 12px",
                                color: "#4ade80",
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: approving ? "not-allowed" : "pointer",
                                opacity: approving ? 0.6 : 1,
                                whiteSpace: "nowrap",
                                minHeight: 34,
                              }}
                            >
                              {approving ? (
                                <Spinner size={13} />
                              ) : (
                                <CheckCircleIcon
                                  style={{ width: 13, height: 13 }}
                                />
                              )}
                              {approving ? "Paying…" : `Pay L${lvl}`}
                              {poolOn && lc > 0 && (
                                <span style={{ fontSize: 9, color: "#86efac" }}>
                                  +{lc} leader{lc > 1 ? "s" : ""}
                                </span>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    }),
                  )}
                </tbody>
              </table>
              <Pagination
                page={pendingPag.page}
                totalPages={pendingPag.totalPages}
                total={pendingPag.total}
                pageSize={10}
                onPage={pendingPag.setPage}
              />
            </div>
          )}
        </Card>

        {/* ── Approved bonuses history ── */}
        <Card className="mb-6" padding="lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h3 className={globalStyle.h5}>
                Approved Level-Up Bonuses History
              </h3>
              {approvedBonuses.length > 0 && (
                <span
                  style={{
                    background: "rgba(34,197,94,0.15)",
                    border: "1.5px solid rgba(74,222,128,0.4)",
                    borderRadius: 99,
                    padding: "1px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#86efac",
                  }}
                >
                  {approvedBonuses.length}
                </span>
              )}
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={loadApprovedBonuses}
              loading={loadingApproved}
            >
              Refresh
            </Button>
          </div>
          {loadingApproved ? (
            <LoadingScreen message="Loading approved bonuses..." />
          ) : approvedBonuses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <ClockIcon className="w-12 h-12 text-gray-600" />
              <p className="text-gray-400 text-sm">No approved bonuses yet.</p>
            </div>
          ) : (
            <div style={sw}>
              <table className="w-full" style={{ minWidth: 760 }}>
                <thead>
                  <tr className="border-b border-gray-800">
                    {[
                      "User",
                      "Tier",
                      "Volume",
                      "Levels Paid",
                      "Total",
                      "Distribution",
                      "Paid At",
                    ].map((h) => (
                      <th
                        key={h}
                        className="py-3 px-4 text-left text-sm font-medium text-gray-400"
                        style={nw}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {approvedPag.paged.map((ab) => (
                    <tr
                      key={ab.referral_id}
                      className="border-b border-gray-800 hover:bg-green-900/10"
                    >
                      <td className="py-3 px-4" style={nw}>
                        <div className="flex items-center gap-2">
                          <div
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: "50%",
                              background: "rgba(34,197,94,0.15)",
                              border: "2px solid #22c55e",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 11,
                              fontWeight: 800,
                              color: "#86efac",
                              flexShrink: 0,
                            }}
                          >
                            {ab.username?.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-white">
                            {ab.username}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4" style={nw}>
                        <span
                          style={{
                            background: "rgba(99,102,241,0.15)",
                            border: "1.5px solid #818cf8",
                            borderRadius: 6,
                            padding: "2px 10px",
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#c7d2fe",
                          }}
                        >
                          L{ab.current_level}
                        </span>
                      </td>
                      <td className="py-3 px-4" style={nw}>
                        <p className="text-blue-400 font-semibold text-sm">
                          {formatXRP(ab.team_volume)} XRP
                        </p>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          {ab.awarded_levels.map((lvl) => (
                            <span
                              key={lvl}
                              style={{
                                background: "rgba(34,197,94,0.12)",
                                border: "1.5px solid rgba(74,222,128,0.4)",
                                borderRadius: 6,
                                padding: "2px 8px",
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#86efac",
                                whiteSpace: "nowrap",
                              }}
                            >
                              ✓L{lvl}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-4" style={nw}>
                        <span className="text-green-400 font-bold">
                          {formatXRP(ab.total_awarded)} XRP
                        </span>
                      </td>
                      <td className="py-3 px-4" style={{ minWidth: 220 }}>
                        {ab.pool_distributions &&
                        ab.pool_distributions.length > 0 ? (
                          <div className="space-y-2">
                            {ab.pool_distributions.map((dist, i) => (
                              <div
                                key={i}
                                style={{
                                  background: "rgba(99,102,241,0.07)",
                                  border: "1px solid rgba(99,102,241,0.2)",
                                  borderRadius: 8,
                                  padding: "7px 10px",
                                }}
                              >
                                <p
                                  style={{
                                    fontSize: 10,
                                    color: "#c7d2fe",
                                    fontWeight: 700,
                                    marginBottom: 4,
                                  }}
                                >
                                  L{dist.level} — {formatXRP(dist.gross_bonus)}{" "}
                                  XRP gross
                                </p>
                                <div style={{ marginBottom: 4 }}>
                                  <p
                                    style={{
                                      fontSize: 9,
                                      color: "#fed7aa",
                                      marginBottom: 2,
                                    }}
                                  >
                                    🏆 Achiever pool{" "}
                                    {formatXRP(dist.achiever_pool)} XRP ÷{" "}
                                    {dist.split_count} ={" "}
                                    {formatXRP(dist.per_person_achiever_share)}{" "}
                                    XRP each
                                  </p>
                                  {dist.minor_leaders &&
                                    dist.minor_leaders.length > 0 && (
                                      <div
                                        style={{
                                          paddingLeft: 8,
                                          marginTop: 2,
                                          borderLeft:
                                            "2px solid rgba(192,132,252,0.3)",
                                        }}
                                      >
                                        {dist.minor_leaders.map((ml) => (
                                          <div
                                            key={ml.user_id}
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 4,
                                              marginTop: 2,
                                            }}
                                          >
                                            <div
                                              style={{
                                                width: 14,
                                                height: 14,
                                                borderRadius: "50%",
                                                background:
                                                  "rgba(192,132,252,0.2)",
                                                border: "1px solid #c084fc",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: 7,
                                                fontWeight: 700,
                                                color: "#e9d5ff",
                                                flexShrink: 0,
                                              }}
                                            >
                                              {ml.username
                                                .charAt(0)
                                                .toUpperCase()}
                                            </div>
                                            <span
                                              style={{
                                                fontSize: 9,
                                                color: "#c084fc",
                                              }}
                                            >
                                              @{ml.username} +
                                              {formatXRP(ml.amount)} XRP
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                </div>
                                <p style={{ fontSize: 9, color: "#c7d2fe" }}>
                                  👥 {formatXRP(dist.contributor_pool_actual)}{" "}
                                  XRP → {dist.contributors_paid} contributors
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span style={{ fontSize: 11, color: "#334155" }}>
                            Full bonus to achiever
                          </span>
                        )}
                      </td>
                      <td
                        className="py-3 px-4 text-gray-400 text-sm"
                        style={nw}
                      >
                        {ab.last_bonus_paid_at
                          ? new Date(ab.last_bonus_paid_at).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              },
                            )
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                page={approvedPag.page}
                totalPages={approvedPag.totalPages}
                total={approvedPag.total}
                pageSize={10}
                onPage={approvedPag.setPage}
              />
            </div>
          )}
        </Card>

        {/* ── Settings summary ── */}
        {!loading && settings && (
          <Card className="mb-6" padding="lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className={globalStyle.h5}>Current Settings</h3>
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => setShowSettingsModal(true)}
                leftIcon={<PencilIcon className="w-4 h-4" />}
              >
                Edit
              </Button>
            </div>
            <div className="space-y-6">
              {/* Depth table */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-3">
                  Per-Depth Config
                </h4>
                <div style={sw}>
                  <table className="w-full" style={{ minWidth: 560 }}>
                    <thead>
                      <tr className="border-b border-gray-800">
                        {[
                          "Depth",
                          "Role",
                          "Commission Rate",
                          "Volume Weight",
                          "Example (100 XRP)",
                        ].map((h) => (
                          <th
                            key={h}
                            className="py-2 px-3 text-left text-xs font-medium text-gray-400"
                            style={nw}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from(
                        { length: Math.max(commLevels, volDepths) },
                        (_, i) => i + 1,
                      ).map((depth) => {
                        const rate = dRates[depth] ?? 0;
                        const weight = dWeights[depth] ?? 0;
                        const pal = dp(depth);
                        const inComm = depth <= commLevels;
                        const inVol = depth <= volDepths;
                        const effW = inVol ? weight : floorW;
                        return (
                          <tr
                            key={depth}
                            className="border-b border-gray-800 hover:bg-gray-800/30"
                          >
                            <td className="py-2 px-3">
                              <div
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  border: `2px solid ${pal.border}`,
                                  background: pal.bg,
                                  color: pal.text,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 9,
                                  fontWeight: 800,
                                }}
                              >
                                F{depth}
                              </div>
                            </td>
                            <td
                              className="py-2 px-3 text-white text-xs"
                              style={nw}
                            >
                              {depth === 1 ? "Direct" : `Depth-${depth}`}
                            </td>
                            <td className="py-2 px-3" style={nw}>
                              {inComm ? (
                                <span className="text-primary font-semibold text-sm">
                                  {(rate * 100).toFixed(2)}%
                                </span>
                              ) : (
                                <span className="text-gray-700 text-xs">—</span>
                              )}
                            </td>
                            <td className="py-2 px-3" style={nw}>
                              {effW > 0 ? (
                                <span
                                  style={{
                                    background: "rgba(245,158,11,0.12)",
                                    border: "1px solid rgba(251,191,36,0.3)",
                                    borderRadius: 6,
                                    padding: "2px 8px",
                                    color: "#fde68a",
                                    fontSize: 11,
                                    fontWeight: 700,
                                  }}
                                >
                                  {(effW * 100).toFixed(0)}%{!inVol ? " ∞" : ""}
                                </span>
                              ) : (
                                <span className="text-gray-700 text-xs">—</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-xs" style={nw}>
                              {inComm && (
                                <span className="text-blue-400">
                                  +{(100 * rate).toFixed(2)} comm.{" "}
                                </span>
                              )}
                              {effW > 0 && (
                                <span className="text-yellow-400">
                                  · {(100 * effW).toFixed(0)} vol.
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Tier table */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-3">
                  Tier Badges & Bonuses
                </h4>
                <div style={sw}>
                  <table className="w-full" style={{ minWidth: 480 }}>
                    <thead>
                      <tr className="border-b border-gray-800">
                        {[
                          "Tier",
                          "Name",
                          "Min Volume",
                          "Gross Bonus",
                          poolOn && "Achiever Pool (÷leaders)",
                          poolOn && "Contributors",
                        ]
                          .filter(Boolean)
                          .map((h) => (
                            <th
                              key={h as string}
                              className="py-2 px-4 text-left text-sm font-medium text-gray-400"
                              style={nw}
                            >
                              {h}
                            </th>
                          ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from(
                        { length: Number(settings.levels) || 5 },
                        (_, i) => i + 1,
                      ).map((level) => {
                        const gb = settings.level_bonuses[level] || 0;
                        return (
                          <tr
                            key={level}
                            className="border-b border-gray-800 hover:bg-gray-800/30"
                          >
                            <td className="py-2 px-4 font-medium text-white">
                              {level}
                            </td>
                            <td className="py-2 px-4 text-gray-300" style={nw}>
                              {settings.level_names[level] || `Level ${level}`}
                            </td>
                            <td className="py-2 px-4 text-gray-300" style={nw}>
                              {formatXRP(
                                settings.level_requirements[level]
                                  ?.min_volume || 0,
                              )}{" "}
                              XRP
                            </td>
                            <td className="py-2 px-4" style={nw}>
                              <p className="text-yellow-500 font-semibold">
                                {formatXRP(gb)} XRP
                              </p>
                              {refCutPct > 0 && gb > 0 && (
                                <p style={{ fontSize: 10, color: "#c084fc" }}>
                                  +{formatXRP((gb * refCutPct) / 100)} XRP
                                  referrer
                                </p>
                              )}
                            </td>
                            {poolOn && (
                              <td className="py-2 px-4" style={nw}>
                                <p
                                  style={{
                                    fontSize: 10,
                                    color: "#fed7aa",
                                    marginBottom: 3,
                                  }}
                                >
                                  🏆 {formatXRP((gb * achevrPct) / 100)} XRP (
                                  {achevrPct.toFixed(0)}%)
                                </p>
                                <div style={{ display: "flex", gap: 3 }}>
                                  {[0, 1, 2].map((n) => (
                                    <span
                                      key={n}
                                      style={{
                                        fontSize: 9,
                                        background: "rgba(249,115,22,0.1)",
                                        border:
                                          "1px solid rgba(249,115,22,0.2)",
                                        borderRadius: 4,
                                        padding: "1px 5px",
                                        color: "#fed7aa",
                                      }}
                                    >
                                      {n === 0
                                        ? "alone"
                                        : n === 1
                                          ? "+1"
                                          : "+2"}
                                      :{" "}
                                      {formatXRP(
                                        (gb * achevrPct) / 100 / (n + 1),
                                      )}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            )}
                            {poolOn && (
                              <td className="py-2 px-4" style={nw}>
                                <span
                                  style={{
                                    background: "rgba(99,102,241,0.12)",
                                    border: "1px solid rgba(129,140,248,0.3)",
                                    borderRadius: 6,
                                    padding: "2px 8px",
                                    color: "#c7d2fe",
                                    fontSize: 11,
                                    fontWeight: 700,
                                  }}
                                >
                                  👥 {formatXRP((gb * contribPct) / 100)} XRP (
                                  {contribPct.toFixed(0)}%)
                                </span>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-400">Signup Bonus</p>
                  <p className="text-lg font-medium text-white">
                    {formatXRP(settings.signup_bonus)} XRP
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Daily Limit</p>
                  <p className="text-lg font-medium text-white">
                    {formatXRP(settings.daily_commission_limit)} XRP
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Referrer Cut</p>
                  <p className="text-lg font-medium">
                    {refCutPct > 0 ? (
                      <span className="text-purple-400">
                        {refCutPct.toFixed(2)}%
                      </span>
                    ) : (
                      <span className="text-gray-600">Off</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Pool Split</p>
                  {poolOn ? (
                    <div
                      style={{
                        display: "flex",
                        gap: 5,
                        marginTop: 4,
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          background: "rgba(249,115,22,0.15)",
                          border: "1px solid #f97316",
                          borderRadius: 6,
                          padding: "2px 8px",
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#fed7aa",
                        }}
                      >
                        🏆 {achevrPct.toFixed(0)}%
                      </span>
                      <span
                        style={{
                          background: "rgba(99,102,241,0.15)",
                          border: "1px solid #818cf8",
                          borderRadius: 6,
                          padding: "2px 8px",
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#c7d2fe",
                        }}
                      >
                        👥 {contribPct.toFixed(0)}%
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-600 text-lg font-medium">
                      Disabled
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* ── Leaderboard ── */}
        <Card padding="lg">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <h3 className={globalStyle.h5}>
                Top Referrers by Weighted Volume
              </h3>
              {filteredLb.length > 0 && (
                <span
                  style={{
                    background: "rgba(99,102,241,0.15)",
                    border: "1.5px solid #818cf8",
                    borderRadius: 99,
                    padding: "1px 10px",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#c7d2fe",
                  }}
                >
                  {lbSearch
                    ? `${filteredLb.length} / ${leaderboard.length}`
                    : leaderboard.length}
                </span>
              )}
            </div>
            <div className="relative shrink-0 w-full sm:w-52">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
              <input
                type="text"
                value={lbSearch}
                onChange={(e) => setLbSearch(e.target.value)}
                placeholder="Search referrers..."
                className="w-full pl-9 pr-8 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500 placeholder-gray-500"
              />
              {lbSearch && (
                <button
                  onClick={() => setLbSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <div style={sw}>
            {loading ? (
              <LoadingScreen message="Loading leaderboard..." />
            ) : (
              <>
                <table className="w-full" style={{ minWidth: 680 }}>
                  <thead>
                    <tr className="border-b border-gray-800">
                      {[
                        "Rank",
                        "Referrer",
                        "Weighted Volume",
                        "Total Sales",
                        "Referrals",
                        "Total Earnings",
                        "Network",
                      ].map((h) => (
                        <th
                          key={h}
                          className="py-3 px-4 text-left text-sm font-medium text-gray-400"
                          style={nw}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lbPag.paged.map((e) => (
                      <tr
                        key={e.id}
                        className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors"
                      >
                        <td className="py-3 px-4">
                          <span
                            className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${e.rank === 1 ? "bg-yellow-900 text-yellow-300" : e.rank === 2 ? "bg-gray-700 text-gray-300" : e.rank === 3 ? "bg-orange-900 text-orange-300" : "bg-gray-800 text-gray-400"}`}
                          >
                            {e.rank}
                          </span>
                        </td>
                        <td className="py-3 px-4" style={nw}>
                          <div className="flex items-center gap-3">
                            <div
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                background: "rgba(99,102,241,0.15)",
                                border: "2px solid #818cf8",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 12,
                                fontWeight: 800,
                                color: "#c7d2fe",
                                flexShrink: 0,
                              }}
                            >
                              {e.username?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-white">
                                {e.username}
                              </p>
                              <p className="text-sm text-gray-400 hidden sm:block">
                                {e.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4" style={nw}>
                          <p className="text-blue-400 font-semibold">
                            {formatXRP(e.team_volume)} XRP
                          </p>
                        </td>
                        <td className="py-3 px-4" style={nw}>
                          <p className="text-orange-400 font-semibold">
                            {formatXRP(e.total_sales ?? 0)} XRP
                          </p>
                        </td>
                        <td className="py-3 px-4 text-white font-medium">
                          {e.total_referrals}
                        </td>
                        <td
                          className="py-3 px-4 text-green-400 font-semibold"
                          style={nw}
                        >
                          {formatXRP(e.total_earnings)} XRP
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => {
                              setTreeUserId(e.id);
                              setTreeUsername(e.username);
                              setTreeOpen(true);
                            }}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              background: "rgba(59,130,246,0.12)",
                              border: "1.5px solid rgba(96,165,250,0.4)",
                              borderRadius: 8,
                              padding: "5px 12px",
                              color: "#93c5fd",
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              minHeight: 34,
                            }}
                          >
                            <UserGroupIcon style={{ width: 13, height: 13 }} />
                            View Tree
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredLb.length === 0 && (
                      <tr>
                        <td
                          colSpan={7}
                          className="py-8 text-center text-gray-400"
                        >
                          {lbSearch ? (
                            <span>
                              No results for "
                              <strong className="text-white">{lbSearch}</strong>
                              " —{" "}
                              <button
                                onClick={() => setLbSearch("")}
                                className="text-indigo-400 hover:underline"
                              >
                                clear
                              </button>
                            </span>
                          ) : (
                            "No leaderboard data"
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <Pagination
                  page={lbPag.page}
                  totalPages={lbPag.totalPages}
                  total={lbPag.total}
                  pageSize={15}
                  onPage={lbPag.setPage}
                />
              </>
            )}
          </div>
        </Card>
      </main>
      <BottomNav />

      {/* ── Tree modal ── */}
      <UserTreeModal
        isOpen={treeOpen}
        onClose={() => {
          setTreeOpen(false);
          setTreeUserId(0);
          setTreeUsername("");
        }}
        userId={treeUserId}
        username={treeUsername}
        heatTopThreshold={heatTop}
        heatMidThreshold={heatMid}
      />

      {/* ── Deposit exclusion modal ── */}
      <Modal
        isOpen={showExcludeModal}
        onClose={() => {
          setShowExcludeModal(false);
          setDepositSearch("");
          setExcludeSuccess(null);
        }}
        title="Fix Test Deposits"
        size="xl"
      >
        <div className="space-y-4">
          <div
            style={{
              background: "rgba(251,191,36,0.08)",
              border: "1.5px solid rgba(251,191,36,0.3)",
              borderRadius: 10,
              padding: "10px 14px",
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
            }}
          >
            <ExclamationTriangleIcon
              style={{
                width: 18,
                height: 18,
                color: "#fbbf24",
                flexShrink: 0,
                marginTop: 1,
              }}
            />
            <p style={{ fontSize: 12, color: "#fde68a", lineHeight: 1.5 }}>
              Excluded deposits are <strong>not counted</strong> in weighted
              team volume or tier calculations. Reversible anytime.
            </p>
          </div>
          {excludeSuccess && (
            <div
              style={{
                background: "rgba(34,197,94,0.1)",
                border: "1.5px solid rgba(74,222,128,0.3)",
                borderRadius: 8,
                padding: "8px 12px",
                fontSize: 12,
                color: "#86efac",
              }}
            >
              ✓ {excludeSuccess}
            </div>
          )}
          <div style={{ position: "relative" }}>
            <MagnifyingGlassIcon
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                width: 15,
                height: 15,
                color: "#64748b",
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              value={depositSearch}
              onChange={(e) => setDepositSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setDepositPage(1);
                  loadDeposits(1, depositSearch);
                }
              }}
              placeholder="Search username or email — press Enter"
              style={{
                width: "100%",
                paddingLeft: 32,
                paddingRight: depositSearch ? 32 : 12,
                paddingTop: 8,
                paddingBottom: 8,
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.09)",
                borderRadius: 8,
                color: "#f1f5f9",
                fontSize: 13,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            {depositSearch && (
              <button
                onClick={() => {
                  setDepositSearch("");
                  loadDeposits(1, "");
                }}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: "#64748b",
                  cursor: "pointer",
                  fontSize: 16,
                  padding: 0,
                }}
              >
                ×
              </button>
            )}
          </div>
          <div style={{ maxHeight: 420, overflowY: "auto" }}>
            {loadingDeposits ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 120,
                }}
              >
                <Spinner size={28} />
              </div>
            ) : deposits.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "32px 0",
                  color: "#64748b",
                  fontSize: 13,
                }}
              >
                {depositSearch
                  ? `No deposits found for "${depositSearch}"`
                  : "No deposits found"}
              </div>
            ) : (
              <div style={sw}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                    minWidth: 520,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        borderBottom: "1px solid rgba(255,255,255,0.07)",
                      }}
                    >
                      {["User", "Amount", "Date", "TX ID", "Action"].map(
                        (h) => (
                          <th
                            key={h}
                            style={{
                              padding: "8px 10px",
                              textAlign: "left",
                              color: "#64748b",
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {deposits.map((dep) => {
                      const isToggling = togglingId === dep.id;
                      return (
                        <tr
                          key={dep.id}
                          style={{
                            borderBottom: "1px solid rgba(255,255,255,0.04)",
                            background: dep.excluded
                              ? "rgba(239,68,68,0.05)"
                              : "transparent",
                          }}
                        >
                          <td style={{ padding: "9px 10px" }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <div
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  background: dep.excluded
                                    ? "rgba(239,68,68,0.15)"
                                    : "rgba(99,102,241,0.15)",
                                  border: `2px solid ${dep.excluded ? "#ef4444" : "#818cf8"}`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 10,
                                  fontWeight: 800,
                                  color: dep.excluded ? "#fca5a5" : "#c7d2fe",
                                  flexShrink: 0,
                                }}
                              >
                                {dep.username?.charAt(0).toUpperCase()}
                              </div>
                              <div style={{ whiteSpace: "nowrap" }}>
                                <p
                                  style={{
                                    color: "#f1f5f9",
                                    fontWeight: 600,
                                    margin: 0,
                                  }}
                                >
                                  {dep.username}
                                </p>
                                <p
                                  style={{
                                    color: "#64748b",
                                    fontSize: 10,
                                    margin: 0,
                                  }}
                                >
                                  {dep.email}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td
                            style={{
                              padding: "9px 10px",
                              color: dep.excluded ? "#6b7280" : "#4ade80",
                              fontWeight: 700,
                              textDecoration: dep.excluded
                                ? "line-through"
                                : "none",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatXRP(dep.amount)} XRP
                          </td>
                          <td
                            style={{
                              padding: "9px 10px",
                              color: "#64748b",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {new Date(dep.created_at).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              },
                            )}
                          </td>
                          <td style={{ padding: "9px 10px" }}>
                            <code
                              style={{
                                fontSize: 10,
                                color: "#94a3b8",
                                background: "rgba(255,255,255,0.04)",
                                padding: "2px 6px",
                                borderRadius: 4,
                              }}
                            >
                              {dep.tx_id
                                ? dep.tx_id.slice(0, 14) +
                                  (dep.tx_id.length > 14 ? "…" : "")
                                : "—"}
                            </code>
                          </td>
                          <td style={{ padding: "9px 10px" }}>
                            <button
                              onClick={() => toggleExclude(dep.id)}
                              disabled={isToggling}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                                background: dep.excluded
                                  ? "rgba(34,197,94,0.10)"
                                  : "rgba(239,68,68,0.10)",
                                border: `1.5px solid ${dep.excluded ? "rgba(74,222,128,0.35)" : "rgba(239,68,68,0.35)"}`,
                                borderRadius: 8,
                                padding: "5px 10px",
                                color: dep.excluded ? "#4ade80" : "#f87171",
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: isToggling ? "not-allowed" : "pointer",
                                opacity: isToggling ? 0.6 : 1,
                                whiteSpace: "nowrap",
                                minHeight: 30,
                              }}
                            >
                              {isToggling ? (
                                <Spinner size={12} />
                              ) : dep.excluded ? (
                                <>
                                  <CheckCircleIcon
                                    style={{ width: 12, height: 12 }}
                                  />{" "}
                                  Reinstate
                                </>
                              ) : (
                                <>
                                  <NoSymbolIcon
                                    style={{ width: 12, height: 12 }}
                                  />{" "}
                                  Exclude
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {depositTotalPages > 1 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingTop: 10,
                borderTop: "1px solid rgba(255,255,255,0.06)",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <span style={{ fontSize: 12, color: "#64748b" }}>
                {depositTotal} total deposits
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  ["‹ Prev", depositPage - 1, depositPage === 1],
                  [
                    "Next ›",
                    depositPage + 1,
                    depositPage === depositTotalPages,
                  ],
                ].map(([label, nextP, disabled]) => (
                  <button
                    key={label as string}
                    onClick={() => {
                      setDepositPage(nextP as number);
                      loadDeposits(nextP as number);
                    }}
                    disabled={disabled as boolean}
                    style={{
                      padding: "4px 12px",
                      borderRadius: 6,
                      border: "1px solid rgba(255,255,255,0.1)",
                      background: disabled
                        ? "transparent"
                        : "rgba(255,255,255,0.05)",
                      color: disabled ? "#334155" : "#94a3b8",
                      cursor: disabled ? "not-allowed" : "pointer",
                      fontSize: 12,
                    }}
                  >
                    {label as string}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              paddingTop: 8,
              borderTop: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <Button
              variant="tertiary"
              onClick={() => {
                setShowExcludeModal(false);
                setDepositSearch("");
                setExcludeSuccess(null);
              }}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Settings modal ── */}
      <Modal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        title="Referral Settings"
        size="xl"
      >
        <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
          {/* Depth counts */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {(
              [
                ["Commission Depths", "commission_levels", 15],
                ["Volume Depth Levels", "volume_depth_levels", 15],
                ["Number of Tiers", "levels", 15],
              ] as const
            ).map(([label, key, max]) => (
              <div key={key} className={globalStyle.formGroup}>
                <label className={globalStyle.label}>{label}</label>
                <select
                  value={formData[key]}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      [key]: parseInt(e.target.value),
                    })
                  }
                  className={globalStyle.input}
                >
                  {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          {/* Per-depth rates/weights */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-1">
              Per-Depth Config
            </h4>
            <div className="space-y-2">
              {Array.from(
                {
                  length: Math.max(
                    formData.commission_levels,
                    formData.volume_depth_levels,
                  ),
                },
                (_, i) => i + 1,
              ).map((depth) => {
                const pal = dp(depth);
                const inComm = depth <= formData.commission_levels;
                const inVol = depth <= formData.volume_depth_levels;
                return (
                  <div
                    key={depth}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-700"
                    style={{ background: pal.bg }}
                  >
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        border: `2px solid ${pal.border}`,
                        background: "#0f172a",
                        color: pal.text,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 11,
                        fontWeight: 800,
                        flexShrink: 0,
                      }}
                    >
                      F{depth}
                    </div>
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        color: pal.text,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {depth === 1 ? "Direct (F1)" : `F${depth}`}
                    </div>
                    <div style={{ minWidth: 110 }}>
                      <label className="text-xs text-blue-400 block mb-1">
                        Commission %
                        {!inComm && (
                          <span className="text-gray-600 ml-1">(off)</span>
                        )}
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={formData.depth_rates[depth] ?? ""}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            depth_rates: {
                              ...p.depth_rates,
                              [depth]: e.target.value,
                            },
                          }))
                        }
                        className={globalStyle.input}
                        placeholder="0.0"
                        disabled={!inComm}
                        style={{ opacity: inComm ? 1 : 0.3 }}
                      />
                    </div>
                    <div style={{ minWidth: 110 }}>
                      <label className="text-xs text-yellow-400 block mb-1">
                        Volume Weight %
                        {!inVol && (
                          <span className="text-gray-600 ml-1">(off)</span>
                        )}
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={formData.depth_weights[depth] ?? ""}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            depth_weights: {
                              ...p.depth_weights,
                              [depth]: e.target.value,
                            },
                          }))
                        }
                        className={globalStyle.input}
                        placeholder="0"
                        disabled={!inVol}
                        style={{ opacity: inVol ? 1 : 0.3 }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tier badges */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-1">
              Tier Badges & Level-Up Bonuses
            </h4>
            <div className="space-y-4">
              {Array.from(
                { length: Number(formData.levels) || 5 },
                (_, i) => i + 1,
              ).map((level) => (
                <div
                  key={level}
                  className="border border-gray-700 rounded-lg p-4 space-y-3"
                >
                  <h5 className="font-medium text-white">Tier {level}</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className={globalStyle.formGroup}>
                      <label className={globalStyle.label}>Tier Name</label>
                      <input
                        type="text"
                        value={formData.level_names[level] || ""}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            level_names: {
                              ...p.level_names,
                              [level]: e.target.value,
                            },
                          }))
                        }
                        className={globalStyle.input}
                        placeholder={`Tier ${level}`}
                      />
                    </div>
                    <div className={globalStyle.formGroup}>
                      <label className={globalStyle.label}>
                        Required Weighted Volume (XRP)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={
                          formData.level_requirements[level]?.min_volume || 0
                        }
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            level_requirements: {
                              ...p.level_requirements,
                              [level]: {
                                min_volume: parseFloat(e.target.value) || 0,
                              },
                            },
                          }))
                        }
                        className={globalStyle.input}
                      />
                    </div>
                    <div className={globalStyle.formGroup}>
                      <label className={globalStyle.label}>
                        Gross Level Bonus (XRP)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        value={formData.level_bonuses[level] || 0}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            level_bonuses: {
                              ...p.level_bonuses,
                              [level]: parseFloat(e.target.value) || 0,
                            },
                          }))
                        }
                        className={globalStyle.input}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Pool split section ── */}
          <div
            style={{
              background: "rgba(99,102,241,0.06)",
              border: "1.5px solid rgba(99,102,241,0.25)",
              borderRadius: 12,
              padding: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 12,
              }}
            >
              <ChartPieIcon
                style={{
                  width: 18,
                  height: 18,
                  color: "#818cf8",
                  flexShrink: 0,
                }}
              />
              <h4 className="text-sm font-semibold text-white">
                Level Bonus Pool Split
              </h4>
            </div>
            <div
              style={{
                background: "rgba(99,102,241,0.06)",
                border: "1px solid rgba(99,102,241,0.15)",
                borderRadius: 8,
                padding: "10px 12px",
                marginBottom: 12,
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
              }}
            >
              <InformationCircleIcon
                style={{
                  width: 15,
                  height: 15,
                  color: "#818cf8",
                  flexShrink: 0,
                  marginTop: 1,
                }}
              />
              <p style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.6 }}>
                When enabled, the gross tier bonus splits:{" "}
                <strong className="text-white">
                  {formData.level_bonus_contributor_pct}%
                </strong>{" "}
                distributed to all downline by deposits ·
                <strong className="text-white">
                  {" "}
                  {formData.level_bonus_achiever_pct}%
                </strong>{" "}
                achiever pool divided equally among achiever + 0–2
                admin-selected minor leaders. The upline referrer cut is{" "}
                <strong className="text-white">
                  always separate and additional
                </strong>
                .
              </p>
            </div>
            <div className="flex items-center gap-3 mb-4 p-3 bg-gray-800/30 rounded-lg">
              <input
                type="checkbox"
                id="pool_enabled"
                checked={formData.level_bonus_pool_enabled}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    level_bonus_pool_enabled: e.target.checked,
                  })
                }
                className="w-4 h-4 text-primary border-gray-700 rounded"
              />
              <label
                htmlFor="pool_enabled"
                className="text-sm text-white cursor-pointer font-medium"
              >
                Enable pool split for tier level-up bonuses
              </label>
            </div>
            {formData.level_bonus_pool_enabled && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div className={globalStyle.formGroup}>
                    <label
                      className={globalStyle.label}
                      style={{ color: "#fb923c" }}
                    >
                      🏆 Achiever Pool (%)
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Split equally among achiever + 0–2 minor leaders.
                    </p>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        max="99"
                        step="1"
                        value={formData.level_bonus_achiever_pct}
                        onChange={(e) => {
                          const v = e.target.value;
                          const n = parseFloat(v) || 0;
                          setFormData({
                            ...formData,
                            level_bonus_achiever_pct: v,
                            level_bonus_contributor_pct: String(
                              Math.max(0, 100 - n),
                            ),
                          });
                        }}
                        className={globalStyle.input}
                        placeholder="30"
                        style={{ paddingRight: 32 }}
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: 12,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#64748b",
                          fontSize: 13,
                          pointerEvents: "none",
                        }}
                      >
                        %
                      </span>
                    </div>
                  </div>
                  <div className={globalStyle.formGroup}>
                    <label
                      className={globalStyle.label}
                      style={{ color: "#818cf8" }}
                    >
                      👥 Contributors Pool (%)
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Distributed to all downline members proportionally by
                      total_deposits.
                    </p>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        max="99"
                        step="1"
                        value={formData.level_bonus_contributor_pct}
                        onChange={(e) => {
                          const v = e.target.value;
                          const n = parseFloat(v) || 0;
                          setFormData({
                            ...formData,
                            level_bonus_contributor_pct: v,
                            level_bonus_achiever_pct: String(
                              Math.max(0, 100 - n),
                            ),
                          });
                        }}
                        className={globalStyle.input}
                        placeholder="70"
                        style={{ paddingRight: 32 }}
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: 12,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#64748b",
                          fontSize: 13,
                          pointerEvents: "none",
                        }}
                      >
                        %
                      </span>
                    </div>
                  </div>
                </div>
                {/* Live preview */}
                <div>
                  <div
                    style={{
                      display: "flex",
                      height: 12,
                      borderRadius: 99,
                      overflow: "hidden",
                      marginBottom: 6,
                      gap: 2,
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(liveA, 100)}%`,
                        background: "#f97316",
                        transition: "width 0.2s",
                        borderRadius: "99px 0 0 99px",
                      }}
                    />
                    <div
                      style={{
                        width: `${Math.min(liveC, 100)}%`,
                        background: "#818cf8",
                        transition: "width 0.2s",
                        borderRadius: "0 99px 99px 0",
                      }}
                    />
                    {!splitOk && (
                      <div style={{ flex: 1, background: "#ef4444" }} />
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, color: "#fed7aa" }}>
                      🏆 Achiever pool: {liveA.toFixed(0)}%
                    </span>
                    <span style={{ fontSize: 11, color: "#c7d2fe" }}>
                      👥 Contributors: {liveC.toFixed(0)}%
                    </span>
                    {!splitOk ? (
                      <span
                        style={{
                          fontSize: 11,
                          color: "#ef4444",
                          fontWeight: 700,
                        }}
                      >
                        ⚠️ Must total 100%
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: "#4ade80" }}>
                        ✓ Totals 100%
                      </span>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Limits */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className={globalStyle.formGroup}>
              <label className={globalStyle.label}>
                Daily Commission Limit (XRP)
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={formData.daily_commission_limit}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    daily_commission_limit: e.target.value,
                  })
                }
                className={globalStyle.input}
              />
            </div>
            <div className={globalStyle.formGroup}>
              <label className={globalStyle.label}>
                Monthly Commission Limit (XRP)
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={formData.monthly_commission_limit}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    monthly_commission_limit: e.target.value,
                  })
                }
                className={globalStyle.input}
              />
            </div>
          </div>
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Payout Schedule</label>
            <select
              value={formData.payout_schedule}
              onChange={(e) =>
                setFormData({ ...formData, payout_schedule: e.target.value })
              }
              className={globalStyle.input}
            >
              {["daily", "weekly", "biweekly", "monthly"].map((s) => (
                <option key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Signup Bonus (XRP)</label>
            <input
              type="number"
              min="0"
              step="0.1"
              value={formData.signup_bonus}
              onChange={(e) =>
                setFormData({ ...formData, signup_bonus: e.target.value })
              }
              className={globalStyle.input}
            />
          </div>

          {/* Referrer cut */}
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>
              Upline Referrer Level-Bonus Cut (%) — Separate &amp; Additional
            </label>
            <p className="text-xs text-gray-500 mb-2">
              When a tier bonus is paid, the direct referrer gets this % as an
              additional payment on top. Set 0 to disable.
            </p>
            <div className="relative">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.level_bonus_referrer_pct}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    level_bonus_referrer_pct: e.target.value,
                  })
                }
                className={globalStyle.input}
                placeholder="0"
                style={{ paddingRight: 32 }}
              />
              <span
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#64748b",
                  fontSize: 13,
                  pointerEvents: "none",
                }}
              >
                %
              </span>
            </div>
          </div>

          {/* Heat map thresholds */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-2">
              Heat Map Thresholds
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className={globalStyle.formGroup}>
                <label className={globalStyle.label}>
                  <span style={{ color: "#f97316" }}>🔥 Top Tier</span> — top X%
                  of contributors
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    max="98"
                    step="1"
                    value={formData.heat_top_threshold}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        heat_top_threshold: e.target.value,
                      })
                    }
                    className={globalStyle.input}
                    placeholder="20"
                    style={{ paddingRight: 32 }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      right: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#64748b",
                      fontSize: 13,
                      pointerEvents: "none",
                    }}
                  >
                    %
                  </span>
                </div>
              </div>
              <div className={globalStyle.formGroup}>
                <label className={globalStyle.label}>
                  <span style={{ color: "#38bdf8" }}>💧 Mid Tier cutoff</span> —
                  top X% = top + mid combined
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="2"
                    max="99"
                    step="1"
                    value={formData.heat_mid_threshold}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        heat_mid_threshold: e.target.value,
                      })
                    }
                    className={globalStyle.input}
                    placeholder="70"
                    style={{ paddingRight: 32 }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      right: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#64748b",
                      fontSize: 13,
                      pointerEvents: "none",
                    }}
                  >
                    %
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-yellow-900/10 border border-yellow-800/30 rounded-lg">
            <input
              type="checkbox"
              id="recalc"
              checked={formData.recalculate_volumes}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  recalculate_volumes: e.target.checked,
                })
              }
              className="w-4 h-4 mt-0.5 text-primary border-gray-700 rounded"
            />
            <div>
              <label
                htmlFor="recalc"
                className="text-sm text-white cursor-pointer"
              >
                Recalculate all weighted volumes after saving
              </label>
              <p className="text-xs text-gray-500 mt-0.5">
                Recommended when depth weights change.
              </p>
            </div>
          </div>
          <div className="flex items-center p-4 bg-gray-800/30 rounded-lg">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) =>
                setFormData({ ...formData, is_active: e.target.checked })
              }
              className="w-4 h-4 text-primary border-gray-700 rounded"
            />
            <label htmlFor="is_active" className="ml-3 text-sm text-white">
              Referral program is active
            </label>
          </div>
          <div className="flex space-x-3 pt-4 border-t border-gray-800">
            <Button
              variant="tertiary"
              onClick={() => setShowSettingsModal(false)}
              fullWidth
            >
              Cancel
            </Button>
            <Button onClick={handleSaveSettings} loading={saving} fullWidth>
              Save Settings
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminReferral;
